// ==========================================
// V4 Frontend API Client
// All requests go through api.vhire.com
// ==========================================

import { JOB_PICKER_VIEW, JobPickerPageSchema, JobPickerRequestSchema, type JobPickerPage } from '@vhire/shared/service-types/job-picker';
import type {
  ApiResponse,
  PaginatedResponse,
  Job,
  JobDetail,
  CandidateProfile,
  ApplicationSummary,
  MatchReport,
  DashboardStats,
  MatchingJob,
  CandidateProgress,
  Quest,
  SkillProgress,
  AssessmentSession,
  AssessmentResult,
  ArenaQuestion,
} from '@/types';

// API validation utilities
import {
  validateArray,
  validateCompleteArray,
  validatePublicJobsPagination,
  validateSafe,
} from './api-validation';
import { RECRUITER_SEARCH_BROWSER_TIMEOUT_MS } from './marketplace-precision';
import { createRequestDeadline } from './request-lifecycle';
import { JobSchema, CandidateProfileSchema, ApplicationSummarySchema } from './schemas';

// API Gateway URL - all frontend requests go through here
import { API_BASE } from './config';

// Recruiter (tenant-admin) token reader — used by admin-only endpoints
// (e.g. share-links). The default getAuthToken() reads the candidate token
// and would 401 every recruiter-initiated request.
import { getAdminToken } from './auth-tokens';

// PL1/PL2 explainability shape for the recruiter comprehensive-match call —
// the single source of truth shared with the drawer + format helpers.
import type { MatchExplain } from './format';
import type {
  MarketplaceJobSource,
  MarketplaceSkillIntent,
} from './marketplace-intent';
import type {
  MarketplaceSearchByJobApiRequest,
  MarketplaceSearchByJobV2Response,
  MarketplaceSearchEnrichedResultV2,
  MarketplaceSearchSkillCoverage,
  MarketplaceSearchSort,
  MarketplaceSearchSpecOverrides,
  MarketplaceSearchSpecV2 as MarketplaceSearchSpecV2Wire,
  MarketplaceSearchV2ApiRequest,
} from '@vhire/shared/service-types/marketplace-search';
import {
  MarketplaceSearchByJobV2ResponseSchema,
  MarketplaceSearchGatewayV2ResponseSchema,
} from '@vhire/shared/service-types/marketplace-search';

// Partner Network (employer-side) request/response shapes — see networkApi below.
import type {
  VendorSearchRequest,
  VendorSearchResponse,
  PublishToPartnersRequest,
  PublishToPartnersResponse,
  PublishDecisionRequest,
  PublishDecisionResponse,
  DistributeJobRequest,
  DistributeJobResponse,
  ListBidsResponse,
  FillScoreResult,
  ListEngagementsRequest,
  ListEngagementsResponse,
  ListTimesheetsRequest,
  ListTimesheetsResponse,
  VerifyTimesheetRequest,
  VerifyTimesheetResponse,
  SettlementInvoiceSummary,
  ListSettlementInvoicesResponse,
  PlacementSummary,
  ListPlacementsResponse,
  PublishApprovalSummary,
  ListPublishApprovalsResponse,
  // P4 vendor-side
  VendorProfileInput,
  GetVendorProfileResponse,
  UpsertVendorProfileResponse,
  ListMyJobsRequest,
  ListMyJobsResponse,
  ListMyEngagementsRequest,
  ListMyEngagementsResponse,
  ListMyPayoutsRequest,
  ListMyPayoutsResponse,
  GetPayoutAccountResponse,
  PayoutAccountInput,
  UpsertPayoutAccountResponse,
  SubmitCandidateRequest,
  SubmitCandidateResponse,
  RecordTimesheetRequest,
  RecordTimesheetResponse,
  // P5 platform-admin
  FeeAgreement,
  ListFeeAgreementsRequest,
  ListFeeAgreementsResponse,
  UpsertFeeAgreementRequest,
  UpsertFeeAgreementResponse,
  ListPlatformVendorsRequest,
  ListPlatformVendorsResponse,
  SetVendorVerificationRequest,
  SetVendorVerificationResponse,
  GenerateInvoiceRequest,
  GenerateInvoiceResponse,
} from './network-types';

// Canonical candidate-login redirect helpers (single source of truth shared
// with middleware + the login pages — see lib/auth-redirect.ts).
import { CANDIDATE_LOGIN_PATH, buildCandidateLoginUrl, storeRedirect } from './auth-redirect';

// Plan refusals (entitlements PR2A 3.6): the one copy map.
import { readEntitlementRefusal } from './entitlement-errors';

// Browser-side `api-error` telemetry for the three shared fetch helpers below.
import { reportClientError } from './telemetry/client-error';

// For local development, you can override with:
// NEXT_PUBLIC_API_URL=http://localhost:8787/api

// ==================== AUTH TOKEN MANAGEMENT ====================

let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (typeof window !== 'undefined') {
    // Determine cookie domain for cross-subdomain auth
    // On vhire.com or *.vhire.com, set domain=.vhire.com so cookie works everywhere
    const hostname = window.location.hostname;
    const isVhireDomain = hostname === 'vhire.com' || hostname.endsWith('.vhire.com');
    const domainAttr = isVhireDomain ? '; domain=.vhire.com' : '';

    if (token) {
      // Store in localStorage for API calls
      localStorage.setItem('vhire_token', token);
      // Also set cookie for middleware auth checks (SSR/navigation)
      // domain=.vhire.com ensures cookie works across all subdomains
      document.cookie = `vhire_token=${token}; path=/${domainAttr}; max-age=${60 * 60 * 24 * 7}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    } else {
      localStorage.removeItem('vhire_token');
      // Clear the cookie (must include same domain to properly clear)
      document.cookie = `vhire_token=; path=/${domainAttr}; max-age=0`;
    }
  }
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  if (typeof window !== 'undefined') {
    return localStorage.getItem('vhire_token');
  }
  return null;
}

// ==================== FETCH WRAPPER ====================

// Backstop timeout for all gateway calls (see usage in fetchApi). 20s is a
// deliberate ceiling: long enough never to trip on healthy requests, short
// enough that a hung backend surfaces as an error instead of an endless spinner.
const DEFAULT_FETCH_TIMEOUT_MS = 20_000;

/**
 * Fire-and-forget `api-error` telemetry for `fetchApi`, `adminFetchApi` and
 * `fetchMarketplace`. It only observes: it never throws, never awaits and never
 * changes what the helper returns, so it is safe to call from any branch.
 *
 * The reported url is the endpoint path with its query string REMOVED — queries
 * carry emails, search terms and signed params (`/apply/session?email=…`) — and
 * is built on API_BASE so `redactTokenPaths` (inside `reportClientError`)
 * recognises credential routes such as `/api/forwards/<token>/…`. The body of
 * the failed response is never read for telemetry.
 *
 * Browser only: on the server there is no client to report on. The reporter
 * posts with sendBeacon, never through these helpers, so it cannot report
 * about itself.
 *
 * Beacon only: `reportClientError` falls back to `fetch` where `sendBeacon` is
 * missing. From inside this failure path that would be a SECOND request through
 * the page's own `fetch` — one that tests which stub or count `fetch` would
 * trip over — so where there is no beacon (jsdom, ancient browsers) the report
 * is simply not made. Every browser VHire supports has `sendBeacon`.
 */
function reportApiFailure(
  endpoint: string,
  options: RequestInit,
  failure: { response: Response } | { error: unknown },
): void {
  try {
    if (typeof window === 'undefined') return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    const path = endpoint.replace(/[?#][\s\S]*$/, '');
    const url = `${API_BASE}${path}`;
    const method = (options.method ?? 'GET').toUpperCase();
    if ('response' in failure) {
      const { response } = failure;
      reportClientError({
        kind: 'api-error',
        status: response.status,
        url,
        error: new Error(`HTTP ${response.status}`),
        requestId: response.headers?.get('x-request-id') ?? undefined,
        context: { method },
      });
      return;
    }
    const errorName = (failure.error as { name?: unknown } | null)?.name;
    const reason = errorName === 'AbortError' || errorName === 'TimeoutError' ? 'aborted' : 'network';
    reportClientError({
      kind: 'api-error',
      url,
      error: failure.error,
      context: { method, reason },
    });
  } catch {
    // Telemetry must never break the request path.
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // Convert an indefinite hang into a clean { success: false }. Composes with
  // any caller-supplied AbortSignal so existing cancellation still works.
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
    DEFAULT_FETCH_TIMEOUT_MS,
  );
  const callerSignal = options.signal;
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true });
  }

  // True once fetch() has resolved: a throw after that point is not a network
  // failure, so the catch below must not report it as one.
  let received = false;
  try {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
      // Note: Using Bearer tokens, not cookies - no credentials needed
    });
    received = true;
    if (!response.ok) reportApiFailure(endpoint, options, { response });

    // Parse defensively: a non-JSON error body (HTML 502/401 page, empty body)
    // must NOT throw here, or the status/401 handling below is bypassed and the
    // session-expiry auto-logout+redirect never runs.
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      // 401 → candidate session expired mid-request. Clear auth and bounce to
      // the CANDIDATE login (/auth/login — NOT the employer /login), preserving
      // where the user was so they return there after re-auth. Redirect target
      // + storage key + login path all come from the single source of truth in
      // lib/auth-redirect so this can never drift from middleware again.
      if (response.status === 401) {
        setAuthToken(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('vhire_user');
          // Don't loop if we're already on a login/auth page.
          const path = window.location.pathname;
          const onAuthPage =
            path === '/login' || path === CANDIDATE_LOGIN_PATH || path.startsWith('/auth');
          if (!onAuthPage) {
            const returnTo = path + window.location.search;
            storeRedirect(returnTo); // same-tab fallback
            window.location.href = buildCandidateLoginUrl({ redirect: returnTo, expired: true });
            return { success: false, error: 'Session expired. Please log in again.' };
          }
        }
      }
      // A plan refusal carries its code in `error`: show the words, keep the code.
      const refusal = readEntitlementRefusal(data);
      if (refusal) return { success: false, error: refusal.message, errorCode: refusal.code };
      return { success: false, error: data.error || 'Request failed' };
    }

    return data;
  } catch (error) {
    if (!received) reportApiFailure(endpoint, options, { error });
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('API Error:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Admin-authenticated variant of fetchApi. Uses the recruiter/tenant-admin
 * token (getAdminToken) instead of the candidate token (getAuthToken). Use for
 * admin-only gateway routes — e.g. the question bank, which is gated as
 * tenant-admin at the gateway. On 401 it does NOT clear candidate auth or
 * redirect to the candidate /login.
 */
async function adminFetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  let received = false;
  try {
    const token = getAdminToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
    received = true;
    if (!response.ok) reportApiFailure(endpoint, options, { response });
    const data = await response.json();
    if (!response.ok) {
      const refusal = readEntitlementRefusal(data);
      if (refusal) return { success: false, error: refusal.message, errorCode: refusal.code, status: response.status };
      return { success: false, error: data.error || 'Request failed', status: response.status };
    }
    return { ...data, status: response.status };
  } catch (error) {
    if (!received) reportApiFailure(endpoint, options, { error });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0, // network / timeout — not an HTTP status; callers treat as a real failure.
    };
  }
}

// ==================== JOB ENDPOINTS ====================
// Gateway: GET /api/jobs, GET /api/jobs/:id

export const jobsApi = {
  /**
   * List jobs with optional filters
   * Gateway: GET /api/jobs
   * Response validated against JobSchema
   */
  list: async (params?: {
    search?: string;
    role?: string;
    location?: string;
    remote?: boolean;
    minSalary?: number;
    maxSalary?: number;
    tenantSlug?: string;
    // Work Mode: Remote, Onsite, Hybrid (multi-select)
    workMode?: ('remote' | 'onsite' | 'hybrid')[];
    // Role Type: Technical, Non-Technical (multi-select)
    roleType?: ('technical' | 'non-technical')[];
    // Job Type: full-time, part-time, contract, internship
    jobType?: 'full-time' | 'part-time' | 'contract' | 'internship';
    // Experience Years Range: 0-2, 2-4, 4-6, 6-8, 8-10, 10-15, 15-20, 20+
    experienceYears?: string;
    limit?: number;
    offset?: number;
    afterTs?: number;
    afterId?: string;
  }, mode: 'search' | 'discovery' = 'search'): Promise<PaginatedResponse<Job> & {
    failureKind?: 'upstream' | 'contract_drift';
    error?: string;
  }> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          // Handle array values for multi-select filters
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, String(v)));
          } else {
            searchParams.set(key, String(value));
          }
        }
      });
    }
    const query = searchParams.toString();
    const response = await fetchApi<{ jobs: Job[]; pagination: { total: number | null; limit: number; offset: number | null; hasMore: boolean; next?: { afterTs?: unknown; afterId?: unknown } | null } }>(
      `/jobs${mode === 'discovery' ? '/discovery' : ''}?${query}`
    );

    const unavailable = (
      failureKind: 'upstream' | 'contract_drift',
      error: string,
    ) => ({
      success: false,
      data: [] as Job[],
      total: null,
      hasMore: false,
      next: null,
      failureKind,
      error,
    });

    if (response.success !== true) {
      return unavailable('upstream', response.error || 'Jobs are temporarily unavailable');
    }

    // A public inventory is atomic at the validation boundary. Never preserve
    // `success: true` while converting a missing/malformed array into `[]`, and
    // never drop one bad row while publishing the remainder as complete.
    const jobsResult = validateCompleteArray(
      response.data?.jobs,
      JobSchema,
      '/api/jobs',
      'jobs'
    );
    if (!jobsResult.success) {
      return unavailable('contract_drift', 'Invalid public jobs response');
    }
    const validatedJobs = jobsResult.data as Job[];

    const paginationResult = validatePublicJobsPagination(response.data?.pagination);
    if (!paginationResult.success) {
      return unavailable('contract_drift', 'Invalid public jobs pagination');
    }
    const { total, hasMore, next } = paginationResult.data;
    return {
      success: response.success,
      data: validatedJobs,
      total,
      hasMore,
      next,
    };
  },

  /**
   * Get single job by ID or slug
   * Gateway: GET /api/jobs/:id
   */
  get: async (jobId: string): Promise<ApiResponse<JobDetail>> => {
    return fetchApi<JobDetail>(`/jobs/${jobId}`);
  },

  /** Record one browser-rendered VHire job-page visit (deduped by the caller). */
  recordView: async (jobId: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/view`, {
        method: 'POST',
        keepalive: true,
      });
      return response.ok;
    } catch {
      return false;
    }
  },

  /**
   * Get matching jobs for a candidate
   * Gateway: GET /api/matches/jobs/:candidateId
   */
  getMatchingJobs: async (candidateId: string, params?: {
    limit?: number;
    minScore?: number;
  }): Promise<ApiResponse<MatchingJob[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.minScore) searchParams.set('minScore', String(params.minScore));
    const query = searchParams.toString();
    const response = await fetchApi<{ matches: MatchingJob[] }>(
      `/matches/jobs/${candidateId}${query ? `?${query}` : ''}`
    );
    return {
      success: response.success,
      data: response.data?.matches,
      error: response.error,
    };
  },

  /**
   * AI-powered semantic job search
   * Gateway: POST /api/search/jobs
   * Uses Cloudflare AI Search with website data source
   */
  aiSearch: async (query: string, options?: {
    tenantSlug?: string;
    maxResults?: number;
    signal?: AbortSignal;
  }): Promise<{
    success: boolean;
    results: Array<{
      jobId: string;
      title: string;
      score: number;
      snippet: string;
      folder: string;
    }>;
    aiSummary?: string;
    query: string;
    totalResults: number;
    searchTimeMs: number;
    error?: string;
  }> => {
    const response = await fetch(`${API_BASE}/search/jobs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        tenantSlug: options?.tenantSlug,
        maxResults: options?.maxResults ?? 25,
      }),
      signal: options?.signal,
    });
    if (!response.ok) {
      throw new Error('AI job search request failed');
    }
    return response.json();
  },

  /**
   * Get match score for a specific job-candidate pair
   * Gateway: GET /api/matches/score/:jobId/:candidateId
   */
  getMatchScore: async (jobId: string, candidateId: string): Promise<ApiResponse<{
    score: number;
    hasEmbeddings: boolean;
    grade?: 'A' | 'B' | 'C' | 'D' | 'F';
    breakdown?: { semantic: number; experience: number; salary: number; location: number; availability: number; engagement: number };
    quality?: number;
    qualityGrade?: 'A' | 'B' | 'C' | 'D' | 'F';
    qualitySignals?: string[];
    qualitySignalsPresent?: number;
    liveness?: 'active' | 'recent' | 'passive' | 'unknown';
    matchReady?: boolean;
    matchReadiness?: 'ready' | 'pending' | 'failed' | 'stale';
    scoreSource?: 'indexed_vector' | 'live_profile_preview';
  }>> => {
    return fetchApi(`/matches/score/${jobId}/${candidateId}`);
  },

  /**
   * Get the FULL comprehensive match for a job↔candidate pair (recruiter/ATS).
   * Gateway: GET /api/matches/comprehensive/:jobId/:candidateId — tenant-admin
   * + job-ownership + candidate-access gated, so this uses the admin token
   * (like the other recruiter calls) NOT the candidate token. Unlike
   * getMatchScore (the light path) the response carries the Quality + Liveness
   * axes and the matched/missing skill gap. Returns the MatchExplain shape + score.
   */
  getComprehensiveMatch: async (
    jobId: string,
    candidateId: string
  ): Promise<ApiResponse<MatchExplain & { score: number }>> => {
    return adminFetchApi(`/matches/comprehensive/${jobId}/${candidateId}`);
  },
};

// ==================== CANDIDATE ENDPOINTS ====================
// Gateway: /api/candidates/*

export const candidateApi = {
  /**
   * Get current candidate profile
   * Gateway: GET /api/candidates/me
   * Requires: Authorization header with session token
   * Response validated against CandidateProfileSchema
   */
  getProfile: async (): Promise<ApiResponse<CandidateProfile>> => {
    const response = await fetchApi<CandidateProfile>('/candidates/me');

    if (response.success && response.data) {
      // Validate profile against schema
      const result = validateSafe(response.data, CandidateProfileSchema);
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.warn('[API] Candidate profile validation issues:', result.error.issues);
      }
    }

    return response;
  },

  /**
   * Get complete candidate profile with work experience, education, certifications
   * Gateway: GET /api/candidates/me/full
   * Returns: { candidate, workExperience, education, certifications, profileCompletionScore }
   */
  getFullProfile: async (): Promise<ApiResponse<{
    candidate: CandidateProfile;
    workExperience: Array<{
      id: string;
      candidate_id: string;
      company_name: string;
      company_location?: string;
      job_title: string;
      employment_type?: string;
      start_date: number;
      end_date?: number | null;
      is_current: boolean;
      duration_months?: number;
      roles_and_responsibilities: string;
      key_achievements?: string;
      display_order?: number;
    }>;
    education: Array<{
      id: string;
      candidate_id: string;
      institution_name: string;
      institution_location?: string;
      degree_type: string;
      field_of_study: string;
      start_date: number;
      end_date?: number | null;
      is_ongoing: boolean;
      grade_value?: string;
      display_order?: number;
    }>;
    certifications: Array<{
      id: string;
      candidate_id: string;
      certification_name: string;
      issuing_organization: string;
      issue_date: number;
      expiry_date?: number | null;
      credential_id?: string;
      credential_url?: string;
      is_verified: boolean;
      display_order?: number;
    }>;
    profileCompletionScore: number;
  }>> => {
    return fetchApi('/candidates/me/full');
  },

  /**
   * Candidate-owned Marketplace discovery consent. Identity and token lifetime
   * are derived by the Gateway; the browser sends only the intended action and
   * never receives the internal one-time consent token.
   */
  updateMarketplaceConsent: async (
    action: 'opt_in' | 'withdraw',
  ): Promise<ApiResponse<{ marketplaceConsent: boolean }>> => {
    return fetchApi('/candidates/me/marketplace-consent', {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  },

  /**
   * Update candidate profile
   * Gateway: PUT /api/candidates/me
   */
  updateProfile: async (
    data: Partial<CandidateProfile>
  ): Promise<ApiResponse<void>> => {
    return fetchApi<void>('/candidates/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get candidate applications
   * Gateway: GET /api/candidates/applications
   * Response validated against ApplicationSummarySchema
   * @param tenantId - Optional: filter to only this tenant's applications (for tenant subdomains)
   */
  getApplications: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
    tenantId?: string; // Filter to specific tenant (for tenant subdomain dashboards)
  }): Promise<ApiResponse<ApplicationSummary[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    const query = searchParams.toString();
    const response = await fetchApi<{ applications: ApplicationSummary[]; total?: number }>(
      `/candidates/applications${query ? `?${query}` : ''}`
    );

    // Validate applications array against schema
    // Note: API returns { success, applications, total } directly (not wrapped in data)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawApplications = (response as unknown as { applications?: ApplicationSummary[] }).applications || response.data?.applications;
    const validatedApplications = validateArray(
      rawApplications,
      ApplicationSummarySchema,
      '/api/candidates/applications',
      'applications'
    ) as ApplicationSummary[];

    return {
      success: response.success,
      data: validatedApplications,
      error: response.error,
    };
  },

  // ==================== RESUME MANAGEMENT ====================

  /**
   * Get all resumes for current candidate (from RESUMES bucket - application uploads)
   * Gateway: GET /api/candidates/me/resumes
   */
  getResumes: async (): Promise<ApiResponse<Array<{
    id: string;
    file_url: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    parsing_status: string;
    is_primary: boolean;
    created_at: string;
  }>>> => {
    return fetchApi('/candidates/me/resumes');
  },

  // ==================== DOCUMENT MANAGEMENT (Jan 8, 2026) ====================

  /**
   * Get all documents for current candidate
   * Gateway: GET /api/candidates/me/documents
   * @param documentType - Optional: filter by document type
   * @param associatedId - Optional: filter by associated work experience/education ID
   */
  getDocuments: async (params?: {
    documentType?: string;
    associatedId?: string;
  }): Promise<ApiResponse<Array<{
    id: string;
    candidateId: string;
    documentType: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    r2Key: string;
    associatedId?: string;
    associatedType?: string;
    tags?: string[];
    documentMonth?: string;
    uploadStatus: string;
    consentGiven: boolean;
    consentTimestamp?: string;
    uploadedAt: string;
    createdAt: string;
  }>>> => {
    const searchParams = new URLSearchParams();
    if (params?.documentType) searchParams.set('type', params.documentType);
    if (params?.associatedId) searchParams.set('associatedId', params.associatedId);
    const query = searchParams.toString();
    return fetchApi(`/candidates/me/documents${query ? `?${query}` : ''}`);
  },

  /**
   * Upload a document for the current candidate
   * Gateway: POST /api/candidates/me/documents
   * @param file - The file to upload
   * @param documentType - Type of document (resume, certificate, education_*, etc.)
   * @param options - Additional options (associatedId, consentGiven, etc.)
   */
  uploadDocument: async (
    file: File,
    documentType: string,
    options?: {
      associatedId?: string;
      associatedType?: string;
      tags?: string[];
      documentMonth?: string;
      consentGiven?: boolean;
    }
  ): Promise<ApiResponse<{
    id: string;
    candidateId: string;
    documentType: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    r2Key: string;
    uploadStatus: string;
    uploadedAt: string;
  }>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (options?.associatedId) formData.append('associatedId', options.associatedId);
    if (options?.associatedType) formData.append('associatedType', options.associatedType);
    if (options?.tags) formData.append('tags', JSON.stringify(options.tags));
    if (options?.documentMonth) formData.append('documentMonth', options.documentMonth);
    if (options?.consentGiven) formData.append('consentGiven', 'true');

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/candidates/me/documents`, {
      method: 'POST',
      body: formData,
      headers,
    });

    const data = await response.json();
    return {
      success: response.ok && data.success,
      data: data.data,
      error: data.error,
    };
  },

  /**
   * Get a single document by ID
   * Gateway: GET /api/candidates/me/documents/:documentId
   */
  getDocument: async (documentId: string): Promise<ApiResponse<{
    id: string;
    candidateId: string;
    documentType: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    r2Key: string;
    uploadStatus: string;
    uploadedAt: string;
  }>> => {
    return fetchApi(`/candidates/me/documents/${documentId}`);
  },

  /**
   * Get document file URL for download/view
   * Gateway: GET /api/candidates/me/documents/:documentId/file
   * ✅ Jan 16, 2026: Returns direct URL - open in new window for viewing
   * ✅ Jan 20, 2026: Includes token in URL for direct browser access (no Auth header needed)
   */
  getDocumentFileUrl: (documentId: string, inline: boolean = false): string => {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (inline) params.set('inline', 'true');
    if (token) params.set('token', token);
    const queryString = params.toString();
    return `${API_BASE}/candidates/me/documents/${documentId}/file${queryString ? `?${queryString}` : ''}`;
  },

  /**
   * @deprecated Use getDocumentFileUrl instead - blob pattern was causing corruption
   * Kept for backwards compatibility
   */
  getDocumentFile: async (documentId: string, inline: boolean = false): Promise<{
    success: boolean;
    blob?: Blob;
    fileName?: string;
    mimeType?: string;
    error?: string;
  }> => {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const inlineParam = inline ? '?inline=true' : '';
      const response = await fetch(`${API_BASE}/candidates/me/documents/${documentId}/file${inlineParam}`, {
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Download failed' }));
        return { success: false, error: error.error || 'Download failed' };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition?.match(/filename="(.+)"/)?.[1] || 'document';
      const mimeType = response.headers.get('Content-Type') || 'application/octet-stream';

      return { success: true, blob, fileName, mimeType };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Delete a document
   * Gateway: DELETE /api/candidates/me/documents/:documentId
   */
  deleteDocument: async (documentId: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    return fetchApi(`/candidates/me/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get resume file URL (from RESUMES bucket - application uploads)
   * Gateway: GET /api/candidates/me/resumes/:resumeId/file
   * Returns a URL that redirects to the resume file (302 redirect to resume-worker)
   * Note: Binary data doesn't serialize correctly over RPC, so we use redirect pattern
   * ✅ Jan 20, 2026: Includes token in URL for direct browser access (no Auth header needed)
   */
  getResumeFileUrl: (resumeId: string): string => {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    const queryString = params.toString();
    return `${API_BASE}/candidates/me/resumes/${resumeId}/file${queryString ? `?${queryString}` : ''}`;
  },

  // ==================== AVATAR MANAGEMENT (Jan 8, 2026) ====================

  /**
   * Upload avatar/profile picture
   * Gateway: POST /api/candidates/me/avatar
   */
  uploadAvatar: async (file: File): Promise<ApiResponse<{ avatarUrl: string }>> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/candidates/me/avatar`, {
      method: 'POST',
      body: formData,
      headers,
    });

    const data = await response.json();
    return {
      success: response.ok && data.success,
      data: data.data,
      error: data.error,
    };
  },

  /**
   * Get avatar URL for current candidate
   * Returns the URL directly (not the image data)
   */
  getAvatarUrl: (candidateId: string): string => {
    return `${API_BASE}/candidates/${candidateId}/avatar`;
  },

  /**
   * Delete avatar
   * Gateway: DELETE /api/candidates/me/avatar
   */
  deleteAvatar: async (): Promise<ApiResponse<{ deleted: boolean }>> => {
    return fetchApi('/candidates/me/avatar', {
      method: 'DELETE',
    });
  },

  // ==================== WORK EXPERIENCE CRUD (Jan 16, 2026) ====================

  addWorkExperience: async (data: {
    companyName: string;
    companyLocation?: string;
    jobTitle: string;
    employmentType?: string;
    startDate: number;
    endDate?: number | null;
    isCurrent?: boolean;
    rolesAndResponsibilities: string;
    keyAchievements?: string;
    skillsUsed?: string[];
  }): Promise<ApiResponse<{ id: string }>> => {
    return fetchApi('/candidates/me/experience', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateWorkExperience: async (
    experienceId: string,
    data: {
      companyName?: string;
      companyLocation?: string;
      jobTitle?: string;
      employmentType?: string;
      startDate?: number;
      endDate?: number | null;
      isCurrent?: boolean;
      rolesAndResponsibilities?: string;
      keyAchievements?: string;
      skillsUsed?: string[];
    }
  ): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/experience/${experienceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteWorkExperience: async (experienceId: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/experience/${experienceId}`, {
      method: 'DELETE',
    });
  },

  // ==================== EDUCATION CRUD (Jan 16, 2026) ====================

  addEducation: async (data: {
    institutionName: string;
    institutionLocation?: string;
    degreeType: string;
    fieldOfStudy: string;
    specialization?: string;
    startDate: number;
    endDate?: number | null;
    isOngoing?: boolean;
    gradeType?: string;
    gradeValue?: string;
  }): Promise<ApiResponse<{ id: string }>> => {
    return fetchApi('/candidates/me/education', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateEducation: async (
    educationId: string,
    data: {
      institutionName?: string;
      institutionLocation?: string;
      degreeType?: string;
      fieldOfStudy?: string;
      specialization?: string;
      startDate?: number;
      endDate?: number | null;
      isOngoing?: boolean;
      gradeType?: string;
      gradeValue?: string;
    }
  ): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/education/${educationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteEducation: async (educationId: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/education/${educationId}`, {
      method: 'DELETE',
    });
  },

  // ==================== CERTIFICATIONS CRUD (Jan 16, 2026) ====================

  addCertification: async (data: {
    certificationName: string;
    issuingOrganization: string;
    issueDate: number;
    expiryDate?: number | null;
    credentialId?: string;
    credentialUrl?: string;
  }): Promise<ApiResponse<{ id: string }>> => {
    return fetchApi('/candidates/me/certifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCertification: async (
    certificationId: string,
    data: {
      certificationName?: string;
      issuingOrganization?: string;
      issueDate?: number;
      expiryDate?: number | null;
      credentialId?: string;
      credentialUrl?: string;
    }
  ): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/certifications/${certificationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteCertification: async (certificationId: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/certifications/${certificationId}`, {
      method: 'DELETE',
    });
  },

  // ==================== PROJECTS CRUD (Jan 31, 2026) ====================

  addProject: async (data: {
    projectName: string;
    description?: string;
    technologies?: string[];
    projectUrl?: string;
    role?: string;
    projectType?: string;
    clientName?: string;
    startDate?: number;
    endDate?: number | null;
  }): Promise<ApiResponse<{ id: string }>> => {
    return fetchApi('/candidates/me/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateProject: async (
    projectId: string,
    data: {
      projectName?: string;
      description?: string;
      technologies?: string[];
      projectUrl?: string;
      role?: string;
      projectType?: string;
      clientName?: string;
      startDate?: number;
      endDate?: number | null;
    }
  ): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteProject: async (projectId: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/projects/${projectId}`, {
      method: 'DELETE',
    });
  },

  // ==================== ACHIEVEMENTS CRUD (Jan 31, 2026) ====================

  addAchievement: async (data: {
    title: string;
    issuer?: string;
    achievementDate?: number;
    description?: string;
  }): Promise<ApiResponse<{ id: string }>> => {
    return fetchApi('/candidates/me/achievements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateAchievement: async (
    achievementId: string,
    data: {
      title?: string;
      issuer?: string;
      achievementDate?: number;
      description?: string;
    }
  ): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/achievements/${achievementId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteAchievement: async (achievementId: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/candidates/me/achievements/${achievementId}`, {
      method: 'DELETE',
    });
  },

  // ==================== PROFILE VIEWS (Feb 1, 2026) ====================

  /**
   * Get profile views stats for current candidate
   * Gateway: GET /api/candidates/me/profile-views
   * Returns: totalViews, weeklyViews
   */
  getProfileViewsStats: async (): Promise<ApiResponse<{
    totalViews: number;
    weeklyViews: number;
  }>> => {
    return fetchApi('/candidates/me/profile-views');
  },

  /**
   * Get dashboard summary (BFF endpoint)
   * Gateway: GET /api/dashboard/summary
   * Returns all dashboard data in one request with KV caching
   */
  getDashboardSummary: async (params?: { tenantId?: string }): Promise<ApiResponse<{
    applications: Array<{
      id: string;
      jobId: string;
      jobTitle: string;
      company: string;
      location?: string;
      status: string;
      appliedAt?: number;
      submittedAt?: number;
      matchScore?: number;
    }>;
    matchingJobs: Array<{
      id: string;
      title: string;
      company: string;
      location?: string;
      salaryRange?: string;
      skills?: string[];
      matchScore?: number;
    }>;
    profileViews: {
      totalViews: number;
      weeklyViews: number;
    };
    onboarding: {
      needsOnboarding: boolean;
      profileCompleteness: number;
    };
    cached: boolean;
    fetchedAt: string;
  }>> => {
    const queryParams = params?.tenantId ? `?tenantId=${params.tenantId}` : '';
    return fetchApi(`/dashboard/summary${queryParams}`);
  },

  // ==================== ONBOARDING (Jan 24, 2026) ====================

  /**
   * Get onboarding status for current candidate
   * Gateway: GET /api/candidates/onboarding-status
   * Returns: needsOnboarding, steps (resume, platformQuestions), profileCompleteness
   */
  getOnboardingStatus: async (): Promise<ApiResponse<{
    needsOnboarding: boolean;
    steps: {
      resume: { completed: boolean; resumeId?: string; createdAt?: string | null; resumeFresh?: boolean };
      platformQuestions: {
        completed: boolean;
        fresh: boolean;
        lastAnsweredAt: string | null;
        answeredCount: number;
        totalRequired: number;
      };
    };
    profileCompleteness: number;
  }>> => {
    return fetchApi('/candidates/onboarding-status');
  },

  /**
   * Complete onboarding with platform answers
   * Gateway: POST /api/candidates/onboarding/complete
   * Saves platform answers and triggers embedding indexing for job matching
   */
  completeOnboarding: async (data: {
    platformAnswers: Record<string, string>;
    resumeId?: string;
  }): Promise<ApiResponse<{
    candidateId: string;
    profileCompleteness: number;
    fieldsUpdated: number;
  }>> => {
    return fetchApi('/candidates/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get matching jobs for current candidate
   * Gateway: GET /api/candidates/matching-jobs
   * Uses embedding service to find semantically similar jobs
   */
  getMatchingJobs: async (params?: {
    limit?: number;
    minScore?: number;
    tenantId?: string;
    tenantSlug?: string;
  }): Promise<ApiResponse<{
    jobs: Array<Job & {
      matchScore: number | null;
      matchGrade?: string;
      matchBreakdown?: Record<string, unknown>;
      scoreable?: boolean;
      missingSignals?: string[];
      matchBasis: 'resume_profile';
    }>;
    requiresResume: boolean;
    matchBasis: 'resume_profile';
    matchExplanation?: string;
    message?: string;
  }>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.minScore) searchParams.set('minScore', String(params.minScore));
    if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
    if (params?.tenantSlug) searchParams.set('tenantSlug', params.tenantSlug);
    const query = searchParams.toString();
    return fetchApi(`/candidates/matching-jobs${query ? `?${query}` : ''}`);
  },
};

// ==================== APPLY FLOW ENDPOINTS ====================
// Gateway: /api/interviews/history/*, /api/jobs/:id/apply/*

export const applyApi = {
  /**
   * Get interview history with Q&A for a candidate
   * Gateway: GET /api/interviews/history/:candidateId
   * Used by: Candidate profile page, Recruiter candidate review
   */
  getInterviewHistory: async (
    candidateId: string,
    options?: { limit?: number; includeQuestions?: boolean }
  ): Promise<ApiResponse<{
    history: Array<{
      interviewId: string;
      jobId: string;
      jobTitle: string;
      companyName: string;
      status: string;
      score?: number;
      recommendation?: string;
      startedAt: number;
      completedAt?: number;
      totalQuestions: number;
      answeredQuestions: number;
      questions?: Array<{
        questionId: string;
        questionText: string;
        questionType: string;
        questionSource: string;
        answer: string;
      }>;
    }>;
  }>> => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.includeQuestions === false) params.set('includeQuestions', 'false');

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return fetchApi(`/interviews/history/${candidateId}${queryString}`);
  },

  /**
   * Start enhanced application with match analysis
   * Gateway: POST /api/jobs/:jobId/apply/start
   * Returns: sessionId, matchScore, matchedSkills, missingSkills, screeningQuestions
   *
   * ✅ ENHANCED (Dec 8, 2025): Now accepts UTM tracking params
   */
  startEnhanced: async (
    jobId: string,
    data: {
      tenantId?: string;
      email: string;
      fullName: string;
      phone?: string;
      // ✅ Returning candidates can reuse a previously-uploaded resume instead of re-uploading
      existingResumeId?: string;
      // UTM tracking params
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      utmTerm?: string;
      utmContent?: string;
      referrerUrl?: string;
      source?: string;
      // Recruiter attribution (P1 — job-share feature)
      // First-touch: derived from ?ref= URL param or vhire_ref cookie set by /j/{code} landing
      sourceShareCode?: string;
    },
    resumeFile: File | null
  ): Promise<ApiResponse<{
    sessionId: string;
    matchScore: number;
    matchAnalysis: {
      matchedSkills: string[];
      missingSkills: string[];
      experienceRelevance: string;
    };
    questions: Array<{
      id: string;
      text: string;
      type: string;
      source: string;
      required?: boolean;
      category?: string;
      options?: string[];
      conditionalField?: { showIf: string; fieldType: string; fieldLabel: string };
    }>;
    parsedData?: {
      fullName?: string;
      email?: string;
      mobile?: string;
      currentRole?: string;
      currentCompany?: string;
      totalExperience?: number;
      skills?: string[];
      location?: string;
    };
  }>> => {
    const formData = new FormData();
    if (data.tenantId) formData.append('tenantId', data.tenantId);
    formData.append('email', data.email);
    formData.append('fullName', data.fullName);
    if (data.phone) formData.append('phone', data.phone);
    if (resumeFile) {
      formData.append('resume', resumeFile);
    } else if (data.existingResumeId) {
      formData.append('existingResumeId', data.existingResumeId);
    }

    // ✅ UTM tracking params
    if (data.utmSource) formData.append('utmSource', data.utmSource);
    if (data.utmMedium) formData.append('utmMedium', data.utmMedium);
    if (data.utmCampaign) formData.append('utmCampaign', data.utmCampaign);
    if (data.utmTerm) formData.append('utmTerm', data.utmTerm);
    if (data.utmContent) formData.append('utmContent', data.utmContent);
    if (data.referrerUrl) formData.append('referrerUrl', data.referrerUrl);
    if (data.source) formData.append('source', data.source);
    // Job-share attribution (P1)
    if (data.sourceShareCode) formData.append('sourceShareCode', data.sourceShareCode);

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/jobs/${jobId}/apply/start`, {
      method: 'POST',
      body: formData,
      headers,
    });

    return response.json();
  },

  /**
   * Complete enhanced application with screening answers
   * Gateway: POST /api/jobs/:jobId/apply/complete
   * Body: { sessionId, screeningAnswers, additionalData?, acceptedTerms?, termsVersion?, answerMeta? }
   */
  completeEnhanced: async (
    jobId: string,
    data: {
      sessionId: string;
      screeningAnswers: Record<string, string>;
      additionalData?: Record<string, unknown>;
      acceptedTerms?: boolean;
      termsVersion?: string;
      answerMeta?: Record<string, { timeTakenMs?: number }>;
    }
  ): Promise<ApiResponse<{
    applicationId: string;
    message: string;
  }>> => {
    return fetchApi(`/jobs/${jobId}/apply/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get existing session for resume flow (Feb 24, 2026 - session resume)
   * Gateway: GET /api/jobs/:jobId/apply/session?email={email}
   * Returns active session data if candidate has an interrupted apply flow
   */
  getSession: async (
    jobId: string,
    email: string
  ): Promise<ApiResponse<{
    sessionId: string;
    matchScore?: number;
    matchAnalysis?: {
      matchedSkills: string[];
      missingSkills: string[];
      experienceRelevance: string;
    };
    questions?: Array<{
      id: string;
      text: string;
      type: string;
      source: string;
      required?: boolean;
      options?: string[];
    }>;
    arenaSessionId?: string;
    // FE-4: present once the backend surfaces existing-application status on the
    // resume/session endpoint. Read defensively on session-restore until then.
    alreadyApplied?: boolean;
    existingApplicationId?: string;
  }>> => {
    return fetchApi(`/jobs/${jobId}/apply/session?email=${encodeURIComponent(email)}`);
  },

  /**
   * Apply page BFF (May 5, 2026)
   * Gateway: GET /api/jobs/:jobId/apply/context
   *
   * Returns onboarding status, full candidate profile, in-flight session (if any),
   * and public job in a single round-trip — replaces three separate calls
   * fired on apply-page mount. Pairs with the 5-min KV cache on getOnboardingStatus.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContext: async (jobId: string): Promise<ApiResponse<{
    onboarding: {
      needsOnboarding: boolean;
      steps: {
        resume: { completed: boolean; resumeId?: string; createdAt?: string | null; resumeFresh?: boolean };
        platformQuestions: {
          completed: boolean;
          fresh: boolean;
          lastAnsweredAt: string | null;
          answeredCount: number;
          totalRequired: number;
        };
      };
      profileCompleteness: number;
      isReturningCandidate: boolean;
      existingAnswers?: Record<string, string>;
    } | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    profile: any | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    session: any | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    job: any | null;
  }>> => {
    return fetchApi(`/jobs/${jobId}/apply/context`);
  },
};

// ==================== APPLICATION ENDPOINTS ====================
// Gateway: /api/applications/*, /api/interviews/:id/match-report

/**
 * Phase 2 — apply-time match report from the backend. Every field is grounded
 * in real analysis; the frontend must NOT invent scores, labels, or recommendations.
 */
export interface ApplyMatchReportFromApi {
  overallScore: number;
  semanticScore: number;
  skillsScore: number;
  matchedSkills: string[];
  missingSkills: string[];
  skillsBreakdown: Array<{
    skill: string;
    matched: boolean;
    matchSource: 'taxonomy' | 'exact' | 'variant' | 'prefix' | 'text' | 'disambiguation' | 'none';
  }>;
  experienceRelevance: 'high' | 'medium' | 'low' | 'unknown';
  experienceAnalysis: {
    candidateYears: number | null;
    requiredMinYears: number | null;
    requiredMaxYears: number | null;
    seniorityMatch: 'below' | 'aligned' | 'above' | 'unknown';
    score: number;
    // Per-dimension honesty flag (2026-06-23). False when `score` is the
    // scorer's neutral default for a no-requirement / unknown dimension (e.g.
    // a job with no experience_min/max yields 1.0). The UI MUST render
    // "No requirement" / "Not measured" instead of a flattering 100% bar.
    // Absent (older backend) → treat as measured to preserve prior behavior.
    measured?: boolean;
  };
  strengths: string[];
  gaps: string[];
  generatedAt: number;
  // Confidence axis (2026-05-20). When `scoreable === false` the UI MUST hide
  // the percentage and grade — the score is mathematically a meaningless ~62%
  // produced by the scorer's neutral-default handling of null fields. Show
  // a "complete your profile" empty state instead, keyed off `missingSignals`
  // (e.g. 'total_experience', 'expected_ctc', 'notice_period_days', 'skills',
  // 'current_location').
  confidence?: 'high' | 'medium' | 'low';
  scoreable?: boolean;
  missingSignals?: string[];
  // Hard-filter outcome (2026-06-23). False when the candidate failed a hard
  // gate (location/visa/etc.) — the embedding service returns score 0 / grade F
  // / filtersPassed:false but scoreable:true, which the gateway floors to a
  // displayed 1%. The candidate UI MUST treat this like scoreable:false (the
  // "still in the running" state), NOT a bold low ring. Absent → not filtered.
  filtersPassed?: boolean;
}

export const applicationApi = {
  /**
   * Get application by ID
   * Gateway: GET /api/applications/:id
   */
  get: async (applicationId: string): Promise<ApiResponse<ApplicationSummary>> => {
    return fetchApi<ApplicationSummary>(`/applications/${applicationId}`);
  },

  /**
   * Get match report for an interview (post-interview, recruiter-facing data)
   * Gateway: GET /api/interviews/:id/match-report
   */
  getMatchReport: async (
    interviewId: string
  ): Promise<ApiResponse<MatchReport>> => {
    return fetchApi<MatchReport>(`/interviews/${interviewId}/match-report`);
  },

  /**
   * Phase 2 — Get the apply-time match report for a submitted application.
   * Returns the persisted backend-computed report. Requires candidate auth.
   * Gateway: GET /api/applications/:applicationId/match-report
   */
  getApplicationMatchReport: async (
    applicationId: string
  ): Promise<ApiResponse<{ report?: ApplyMatchReportFromApi }>> => {
    return fetchApi(`/applications/${applicationId}/match-report`);
  },

  /**
   * Phase 2 — Get the apply-time match report for an in-flight session.
   * No auth; sessionId itself is the bearer. Used by the success page during
   * the short window before session cleanup.
   * Gateway: GET /api/applications/session/:sessionId/match-report
   */
  getSessionMatchReport: async (
    sessionId: string
  ): Promise<ApiResponse<{ report?: ApplyMatchReportFromApi }>> => {
    return fetchApi(`/applications/session/${sessionId}/match-report`);
  },
};

// ==================== TENANT ENDPOINTS ====================
// Gateway: /api/tenants/*

export const tenantApi = {
  /**
   * Get tenant questions for apply flow
   * Gateway: GET /api/tenants/:tenantId/questions
   * Returns form fields and screening questions configured by the company
   */
  getQuestions: async (
    tenantIdOrSlug: string
  ): Promise<ApiResponse<{
    formFields: Array<{
      id: string;
      fieldId: number;
      name: string;
      label: string;
      type: string;
      isSelected: boolean;
      isMandatory: boolean;
      formCategory: string;
      dropdownValues: string | null;
      displayOrder: number;
    }>;
    screeningQuestions: Array<{
      id: string;
      questionText: string;
      questionType: string;
      options: string[] | null;
      isRequired: boolean;
      displayOrder: number;
    }>;
    questions: Array<{
      id: string;
      text: string;
      type: string;
      source: 'tenant' | 'job';
      required: boolean;
      options?: string[];
      category?: string;
    }>;
  }>> => {
    return fetchApi(`/tenants/${tenantIdOrSlug}/questions`);
  },
};

// ==================== DASHBOARD ENDPOINTS ====================
// Uses candidate endpoints combined with matching

// ==================== ARENA ENDPOINTS ====================
// Gateway: /api/talent/arena/*

export const arenaApi = {
  /**
   * Get candidate progress (gems, league, streak)
   * Gateway: GET /api/talent/arena/progress/:candidateId
   */
  getProgress: async (candidateId: string): Promise<ApiResponse<CandidateProgress>> => {
    return fetchApi<CandidateProgress>(`/talent/arena/progress/${candidateId}`);
  },

  /**
   * Get active quests for candidate
   * Gateway: GET /api/talent/arena/quests/:candidateId
   */
  getQuests: async (candidateId: string): Promise<ApiResponse<{ quests: Quest[] }>> => {
    return fetchApi<{ quests: Quest[] }>(`/talent/arena/quests/${candidateId}`);
  },

  /**
   * Start an Arena assessment session
   * Gateway: POST /api/talent/arena/start
   */
  startSession: async (data: {
    candidateId: string;
    skillId: string;
    difficulty?: 'adaptive' | 'easy' | 'medium' | 'hard';
  }): Promise<ApiResponse<AssessmentSession>> => {
    return fetchApi<AssessmentSession>('/talent/arena/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Submit an answer for an active session
   * Gateway: POST /api/talent/session/:sessionId/submit
   * Returns: { correct, theta, complete, result? }
   */
  submitAnswer: async (
    sessionId: string,
    data: {
      questionId: string;
      answer: string;
      timeTakenMs: number;
    }
  ): Promise<ApiResponse<{
    correct: boolean;
    theta: number;
    complete: boolean;
    result?: AssessmentResult;
  }>> => {
    return fetchApi(`/talent/session/${sessionId}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get next question for an active session
   * Gateway: GET /api/talent/session/:sessionId/next
   * Returns: { question, isLast } from adaptive engine
   */
  getNextQuestion: async (sessionId: string): Promise<ApiResponse<{
    question: ArenaQuestion;
    isLast: boolean;
  }>> => {
    return fetchApi(`/talent/session/${sessionId}/next`);
  },

  /**
   * Get session result
   * Gateway: GET /api/talent/session/:sessionId/result
   */
  getSessionResult: async (sessionId: string): Promise<ApiResponse<AssessmentResult>> => {
    return fetchApi<AssessmentResult>(`/talent/session/${sessionId}/result`);
  },

  /**
   * Get all skill progress for a candidate
   * Gateway: GET /api/talent/arena/skills/:candidateId
   */
  getAllSkillProgress: async (candidateId: string): Promise<ApiResponse<SkillProgress[]>> => {
    return fetchApi(`/talent/arena/skills/${candidateId}`);
  },

  /**
   * Get skill categories for browsing
   * Gateway: GET /api/talent/skills/categories
   */
  getSkillCategories: async (): Promise<ApiResponse<string[]>> => {
    return fetchApi('/talent/skills/categories');
  },

  /**
   * Search skills by name
   * Gateway: GET /api/talent/skills/search?q=...
   */
  searchSkills: async (query: string): Promise<ApiResponse<unknown>> => {
    return fetchApi(`/talent/skills/search?q=${encodeURIComponent(query)}`);
  },
};

export const dashboardApi = {
  /**
   * Get global stats
   * Gateway: GET /api/stats/global
   */
  getGlobalStats: async (): Promise<ApiResponse<DashboardStats>> => {
    return fetchApi<DashboardStats>('/stats/global');
  },

  /**
   * Get applications (via candidate API)
   * Gateway: GET /api/candidates/applications
   */
  getApplications: async (): Promise<ApiResponse<ApplicationSummary[]>> => {
    return candidateApi.getApplications();
  },

  /**
   * Get matching jobs for candidate
   * Gateway: GET /api/matches/jobs/:candidateId
   */
  getMatchingJobs: async (candidateId: string): Promise<ApiResponse<MatchingJob[]>> => {
    return jobsApi.getMatchingJobs(candidateId);
  },
};

// ==================== AUTH ENDPOINTS ====================
// Gateway: /api/auth/*

export const authApi = {
  /**
   * Check if email exists in candidate database
   * Gateway: POST /api/auth/candidate/check-email
   * Returns profile summary for "We found your profile" UX
   */
  checkEmail: async (email: string): Promise<ApiResponse<{
    exists: boolean;
    firstName?: string;
    profileSummary?: {
      name: string | null;
      hasResume: boolean;
      skills: string[];
      experienceYears: number | null;
      sources: string[];
    };
  }>> => {
    return fetchApi('/auth/candidate/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Register new candidate + send magic link
   * Gateway: POST /api/auth/candidate/register
   * Used for new user registration
   * @param data.tenantSlug - Optional tenant slug for email branding
   * @param data.companyName - Optional company name for email branding
   */
  register: async (data: {
    email: string;
    fullName: string;
    mobile: string;
    jobId?: string;
    redirectUrl?: string;
    tenantSlug?: string;
    companyName?: string;
  }): Promise<ApiResponse<void>> => {
    return fetchApi('/auth/candidate/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Send magic link for candidate login (existing user)
   * Gateway: POST /api/auth/candidate/magic-link
   * @param email - Candidate email
   * @param jobId - Optional job ID for context
   * @param redirectUrl - Optional redirect URL after verification
   * @param tenantSlug - Optional tenant slug for email branding (e.g., 'bootminds')
   * @param companyName - Optional company name for email branding (e.g., 'Bootminds Technologies')
   */
  sendMagicLink: async (
    email: string,
    jobId?: string,
    redirectUrl?: string,
    tenantSlug?: string,
    companyName?: string
  ): Promise<ApiResponse<void>> => {
    return fetchApi<void>('/auth/candidate/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, jobId, redirectUrl, tenantSlug, companyName }),
    });
  },

  /**
   * Verify magic link token
   * Gateway: POST /api/auth/candidate/verify
   */
  verifyMagicLink: async (
    token: string
  ): Promise<ApiResponse<{ candidateId: string; sessionToken: string }>> => {
    return fetchApi('/auth/candidate/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  /**
   * Get or create candidate by email (for quick apply)
   * Gateway: POST /api/auth/candidate/quick-auth
   */
  quickAuth: async (email: string): Promise<ApiResponse<{
    candidateId: string;
    isNew: boolean;
    sessionToken: string;
  }>> => {
    return fetchApi('/auth/candidate/quick-auth', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};

// ==================== VAULT API ====================

export const vaultApi = {
  /**
   * Get pending assessments for candidate (invites + self-assessments)
   * Gateway: GET /api/vault/me/assessments
   */
  getAssessments: async (): Promise<ApiResponse<{
    invites: Array<{
      sessionId: string;
      skillId: string;
      skillName: string;
      tenantId: string | null;
      jobId: string | null;
      assessmentType: string;
      scheduledAt: string;
      expiresAt: string;
      durationMinutes: number;
    }>;
    selfAssessments: Array<{
      skillId: string;
      skillName: string;
      available: boolean;
      lastAttempt: string | null;
      bestScore: number | null;
      badgeTier: number | null;
    }>;
  }>> => {
    return fetchApi('/vault/me/assessments');
  },

  /**
   * Get assessment history for candidate
   * Gateway: GET /api/vault/me/history
   */
  getHistory: async (): Promise<ApiResponse<{
    history: Array<{
      sessionId: string;
      skillId: string;
      skillName: string;
      status: 'completed' | 'terminated' | 'expired';
      source: string;
      score: number | null;
      badgeTier: number | null;
      trustLevel: 'high' | 'medium' | 'low' | null;
      completedAt: string | null;
      durationMinutes: number;
      violationCount: number;
    }>;
  }>> => {
    return fetchApi('/vault/me/history');
  },

  /**
   * Record consent before starting assessment
   * Gateway: POST /api/vault/me/sessions/:sessionId/consent
   */
  recordConsent: async (
    sessionId: string,
    consent: { terms: boolean; camera: boolean; screen: boolean; recording: boolean }
  ): Promise<ApiResponse<{ success: boolean }>> => {
    return fetchApi(`/vault/me/sessions/${sessionId}/consent`, {
      method: 'POST',
      body: JSON.stringify(consent),
    });
  },

  /**
   * Get detailed assessment report
   * Gateway: GET /api/vault/me/sessions/:sessionId/report
   */
  getReport: async (sessionId: string): Promise<ApiResponse<{
    sessionId: string;
    skillName: string;
    completedAt: string;
    duration: number;
    score: number;
    badgeTier: number | null;
    trustLevel: 'high' | 'medium' | 'low';
    trustScore: number;
    sections: Array<{
      name: string;
      type: string;
      score: number;
      questionsAttempted: number;
      questionsTotal: number;
      avgTimePerQuestion: number;
    }>;
    strengths: string[];
    improvements: string[];
    violations: Array<{
      type: string;
      count: number;
      severity: string;
    }>;
    percentile: number;
  }>> => {
    return fetchApi(`/vault/me/sessions/${sessionId}/report`);
  },

  /**
   * Get session details for entry page
   * Gateway: GET /api/vault/session/:sessionId
   */
  getSession: async (sessionId: string): Promise<ApiResponse<{
    session: {
      sessionId: string;
      candidateId: string;
      skillId: string;
      status: string;
      scheduledAt: string;
      startedAt: string | null;
      completedAt: string | null;
      identityVerified: boolean;
      proctoringScore: number;
    };
    assessmentConfig: {
      typeId: string;
      name: string;
      questionCount: number;
      timeLimit: number;
      proctoringLevel: string;
      contentTypes: string[];
      codingEnabled: boolean;
      description: string;
    };
  }>> => {
    return fetchApi(`/vault/session/${sessionId}`);
  },

  /**
   * Start assessment session
   * Gateway: POST /api/vault/session/:sessionId/start
   */
  startSession: async (sessionId: string): Promise<ApiResponse<{
    session: {
      sessionId: string;
      status: string;
      startedAt: string;
    };
  }>> => {
    return fetchApi(`/vault/session/${sessionId}/start`, {
      method: 'POST',
    });
  },
};

// ==================== CREDITS API ====================

export const creditsApi = {
  /**
   * Get candidate credit balance
   * Gateway: GET /api/credits/me/balance
   */
  getBalance: async (): Promise<ApiResponse<{ credits: number }>> => {
    return fetchApi('/credits/me/balance');
  },

  /**
   * Get credit transaction history
   * Gateway: GET /api/credits/me/transactions
   */
  getTransactions: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{
    transactions: Array<{
      id: string;
      amount: number;
      type: 'purchase' | 'consume' | 'refund' | 'bonus';
      description: string;
      referenceId?: string;
      createdAt: string;
    }>;
    total: number;
  }>> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return fetchApi(`/credits/me/transactions${query ? `?${query}` : ''}`);
  },

  /**
   * Initiate credit purchase payment
   * Gateway: POST /api/credits/purchase
   */
  initiatePayment: async (packageId: string): Promise<ApiResponse<{
    paymentUrl: string;
    orderId: string;
  }>> => {
    return fetchApi('/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({ packageId }),
    });
  },

  /**
   * Verify payment completion (called by payment callback)
   * Gateway: POST /api/credits/verify
   */
  verifyPayment: async (params: {
    orderId: string;
    paymentId: string;
    signature: string;
  }): Promise<ApiResponse<{
    success: boolean;
    creditsAdded: number;
    newBalance: number;
  }>> => {
    return fetchApi('/credits/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

// ==================== QUESTION BANK API (Phase 3: Question Visibility) ====================

export interface QuestionListItem {
  id: string;
  text: string;
  type: string;
  difficulty: number;
  skillId: string;
  topicTag: string | null;
  industryDomain: string | null;
  seniorityLevel: string | null;
  stats: {
    timesUsed: number;
    passRate: number | null;
    avgTimeMs: number | null;
    discriminationIndex: number;
  };
}

export interface QuestionFilters {
  skillId?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  type?: 'mcq' | 'code' | 'open' | 'scenario';
  topic?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface SkillWithCounts {
  skillId: string;
  questionCount: number;
  easyCount: number;
  mediumCount: number;
  hardCount: number;
}

export const questionBankApi = {
  /**
   * Get paginated questions with filters
   * Gateway: GET /api/tenants/:tenantId/question-bank/questions
   */
  getQuestions: async (tenantId: string, filters: QuestionFilters = {}): Promise<ApiResponse<{
    questions: QuestionListItem[];
    total: number;
    page: number;
    totalPages: number;
  }>> => {
    const params = new URLSearchParams();
    if (filters.skillId) params.set('skillId', filters.skillId);
    if (filters.difficulty) params.set('difficulty', filters.difficulty);
    if (filters.type) params.set('type', filters.type);
    if (filters.topic) params.set('topic', filters.topic);
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    return adminFetchApi(`/tenants/${tenantId}/question-bank/questions${query ? `?${query}` : ''}`);
  },

  /**
   * Get all skills with question counts
   * Gateway: GET /api/tenants/:tenantId/question-bank/skills
   */
  getSkillsWithCounts: async (tenantId: string): Promise<ApiResponse<{
    skills: SkillWithCounts[];
  }>> => {
    return adminFetchApi(`/tenants/${tenantId}/question-bank/skills`);
  },

  /**
   * Get a single question by ID
   * Gateway: GET /api/tenants/:tenantId/question-bank/questions/:questionId
   */
  getQuestionById: async (tenantId: string, questionId: string): Promise<ApiResponse<{
    question: QuestionListItem & {
      options: Array<{ id: string; text: string; isCorrect: boolean }> | null;
      correctAnswer: string;
      explanation: string;
      timeLimit: number;
      tags: string[];
    };
  }>> => {
    return adminFetchApi(`/tenants/${tenantId}/question-bank/questions/${questionId}`);
  },

  /**
   * Get topics for a specific skill
   * Gateway: GET /api/tenants/:tenantId/question-bank/skills/:skillId/topics
   */
  getTopicsForSkill: async (tenantId: string, skillId: string): Promise<ApiResponse<{
    topics: string[];
  }>> => {
    return adminFetchApi(`/tenants/${tenantId}/question-bank/skills/${skillId}/topics`);
  },
};

// ==================== CANDIDATE AGENT API ====================

export const agentApi = {
  /**
   * Streaming chat — returns a ReadableStream of SSE events.
   * Uses raw fetch (not fetchApi) because the response is a stream, not JSON.
   */
  chat: async (message: string, conversationId?: string): Promise<Response> => {
    const token = getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return fetch(`${API_BASE}/agent/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, conversationId }),
    });
  },

  /** List all conversations for the current candidate. */
  getConversations: async (): Promise<ApiResponse<{
    conversations: Array<{
      id: string;
      candidateId: string;
      title: string | null;
      status: string;
      messageCount: number;
      createdAt: string;
      updatedAt: string;
    }>;
  }>> => {
    return fetchApi('/agent/conversations');
  },

  /** Get a conversation with all messages. */
  getConversation: async (conversationId: string): Promise<ApiResponse<{
    conversation: Record<string, unknown>;
    messages: Array<{
      id: string;
      role: string;
      content: string;
      createdAt: string;
      metadata: Record<string, unknown>;
    }>;
  }>> => {
    return fetchApi(`/agent/conversations/${conversationId}`);
  },

  /** Archive (soft delete) a conversation. */
  archiveConversation: async (conversationId: string): Promise<ApiResponse<{ success: boolean }>> => {
    return fetchApi(`/agent/conversations/${conversationId}`, { method: 'DELETE' });
  },
};

// ============================================================================
// Share Links (P1 + P2 — May 2026)
// Recruiter-attributed job sharing with AI-generated LinkedIn-ready copy.
// ============================================================================

export type ShareLinkTone = 'professional' | 'casual' | 'enthusiastic';

export interface ShareLinkCopy {
  short: string | null;
  long: string | null;
  emailSubject: string | null;
  emailBody: string | null;
  tone: ShareLinkTone | null;
  model: string | null;
  generatedAt: string | null;
}

export interface MintShareLinkResponse {
  shareCode: string;
  shareUrl: string;
  status: 'created' | 'existing' | 'reactivated';
  copy: ShareLinkCopy;
}

// Gateway share-links routes return errors as `{ code, message }` objects,
// but ApiResponse declares `error?: string`. Without this normalize step,
// passing the response straight to a React child (e.g. `<p>{error}</p>`)
// throws "Objects are not valid as a React child" and trips the admin
// error boundary on any non-2xx response.
function normalizeShareLinkResponse<T>(raw: unknown): ApiResponse<T> {
  const r = raw as { success?: boolean; data?: T; error?: unknown };
  if (r.success === false && r.error && typeof r.error === 'object') {
    const e = r.error as { code?: string; message?: string };
    return { success: false, error: e.message || e.code || 'Request failed' };
  }
  return r as ApiResponse<T>;
}

export interface ShareQrResponse {
  /** Inline SVG markup of the QR code. */
  svg: string;
  /** Stable public URL of the stored SVG asset. */
  url: string;
  cached?: boolean;
}

export interface CareersShareResponse {
  code: string;
  shareUrl: string;
  destination: string;
  qrUrl: string;
  svg: string;
  status: 'created' | 'existing';
}

/**
 * Share Platform API (generic share_objects spine). Distinct from
 * shareLinksApi (recruiter-attributed /j/ job links): this renders QR-SVGs and
 * manages the tenant careers share. Reuses normalizeShareLinkResponse for the
 * gateway's { code, message } error shape.
 */
export const shareApi = {
  /** Render a QR-SVG for a VHire share URL (e.g. an existing /j/{code} job link). */
  qr: async (body: { url: string; darkColor?: string }): Promise<ApiResponse<ShareQrResponse>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/share/qr`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    return normalizeShareLinkResponse<ShareQrResponse>(await res.json());
  },

  /** Get-or-create the tenant's careers-page share + its QR (entity_type=company). */
  careers: async (): Promise<ApiResponse<CareersShareResponse>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/share/careers`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
    return normalizeShareLinkResponse<CareersShareResponse>(await res.json());
  },
};

export interface PersonCard {
  id: string;
  slug: string;
  fullName: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
  bio: string | null;
  links: Record<string, string>;
  isPublic: boolean;
  shareCode?: string | null;
  shareUrl?: string | null;
  scanCount?: number;
  /** 'person' = login-less tenant_people card; 'recruiter' = a seat-holder's self-managed /r/ card. */
  source?: 'person' | 'recruiter';
  /** False for recruiter cards — their identity is edited by the owner in My Profile, not here. */
  editable?: boolean;
}

export interface PersonQr {
  shareUrl: string;
  code: string | null;
  qrUrl: string | null;
  svg: string | null;
}

/** Result of creating a person. `linked` is set when the email matched an existing team member (no card created). */
export interface CreatePersonResult {
  person: PersonCard | null;
  qr: PersonQr | null;
  linked?: boolean;
  recruiter?: { name: string | null; slug: string | null; isPublic: boolean };
}

export interface PersonInput {
  fullName: string;
  slug?: string;
  jobTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  bio?: string | null;
  links?: Record<string, string>;
  isPublic?: boolean;
  userId?: string | null;
}

/** People / digital business cards (Share Platform Phase 2). Admin-authed; share_platform-gated. */
export const peopleApi = {
  list: async (): Promise<ApiResponse<{ people: PersonCard[] }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/people`, { headers });
    return normalizeShareLinkResponse<{ people: PersonCard[] }>(await res.json());
  },
  create: async (body: PersonInput): Promise<ApiResponse<CreatePersonResult>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/people`, { method: 'POST', headers, body: JSON.stringify(body) });
    return normalizeShareLinkResponse<CreatePersonResult>(await res.json());
  },
  update: async (
    id: string,
    body: Partial<PersonInput>,
  ): Promise<ApiResponse<{ person: PersonCard; qr: PersonQr | null }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/people/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });
    return normalizeShareLinkResponse<{ person: PersonCard; qr: PersonQr | null }>(await res.json());
  },
  remove: async (id: string): Promise<ApiResponse<Record<string, never>>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/people/${id}`, { method: 'DELETE', headers });
    return normalizeShareLinkResponse<Record<string, never>>(await res.json());
  },
  uploadPhoto: async (id: string, file: File): Promise<ApiResponse<{ url: string }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/people/${id}/photo`, { method: 'POST', headers, body: fd });
    return normalizeShareLinkResponse<{ url: string }>(await res.json());
  },
};

// ==================== RECRUITER PROFILE (Share Platform P3) ====================
// The logged-in recruiter's own profile (Neon `users`), edited under
// /admin/profile and published to a public /r/{slug} card. Gateway routes:
//   GET/PUT /api/tenant-admin/profile, POST /api/tenant-admin/profile/avatar,
//   POST /api/tenant-admin/profile/publish. All tenant-admin authed.

export interface RecruiterProfile {
  id?: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  title: string | null;
  linkedinUrl: string | null;
  bio: string | null;
  slug: string | null;
  isPublic: boolean;
  links: Record<string, string>;
  /** Private ID-card fields (badge-only; never shown on the public /r/ card). */
  idCard?: RecruiterIdCard;
  /**
   * The name candidates see in the From line of email this recruiter sends.
   * null = fall back to `name`. Kept separate because `name` is the ACCOUNT
   * identity (audit trails, team list, ATS-imported legal names) and should not
   * have to change to fix how an email reads.
   */
  emailFromName?: string | null;
}

export interface RecruiterIdCard {
  blood_group?: string;
  emergency_name?: string;
  emergency_phone?: string;
  emergency_relation?: string;
}

export interface RecruiterProfilePatch {
  name?: string | null;
  phone?: string | null;
  title?: string | null;
  linkedin_url?: string | null;
  bio?: string | null;
  profile_links?: Record<string, string>;
  profile_slug?: string | null;
  /** Only `false` (unpublish) is honored via update; publish via `publish()`. */
  profile_public?: boolean;
  /** Private ID-card fields (badge-only; excluded from the public card DTO). */
  id_card?: RecruiterIdCard;
  /** Empty string clears the override and reverts to `name`. */
  email_from_name?: string | null;
}

export interface RecruiterShare {
  shareUrl: string;
  code: string | null;
  qrUrl: string | null;
  svg: string | null;
}

export const recruiterProfileApi = {
  /** Load the authenticated recruiter's own profile. */
  get: (): Promise<ApiResponse<{ profile: RecruiterProfile; scanCount?: number; shareUrl?: string }>> =>
    adminFetchApi<{ profile: RecruiterProfile; scanCount?: number; shareUrl?: string }>('/tenant-admin/profile'),

  /** Partial update — only provided fields change. */
  update: (patch: RecruiterProfilePatch): Promise<ApiResponse<{ profile: RecruiterProfile }>> =>
    adminFetchApi<{ profile: RecruiterProfile }>('/tenant-admin/profile', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  /** Upload an avatar (multipart; browser sets the boundary — no JSON Content-Type). */
  uploadAvatar: async (file: File): Promise<ApiResponse<{ url: string }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_BASE}/tenant-admin/profile/avatar`, { method: 'POST', headers, body: fd });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data?.error?.message || data?.error || 'Upload failed', status: res.status };
      return { ...data, status: res.status };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Network error', status: 0 };
    }
  },

  /** Publish the profile publicly + get-or-create its QR share. */
  publish: (): Promise<ApiResponse<{ profile: RecruiterProfile; share: RecruiterShare | null }>> =>
    adminFetchApi<{ profile: RecruiterProfile; share: RecruiterShare | null }>('/tenant-admin/profile/publish', {
      method: 'POST',
    }),
};

export const shareLinksApi = {
  /**
   * Mint or reactivate a share link for the authenticated recruiter on a job.
   * Idempotent: re-calling with the same (job, recruiter) returns the same code.
   * On 'created', auto-generates AI copy unless generateCopy=false is passed.
   *
   * Long timeout (45s) because mint may wait synchronously for AI generation.
   */
  mint: async (
    body: { jobId: string; recruiterId?: string; generateCopy?: boolean; copyTone?: ShareLinkTone },
  ): Promise<ApiResponse<MintShareLinkResponse>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 45000);
    try {
      const res = await fetch(`${API_BASE}/share-links`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      return normalizeShareLinkResponse<MintShareLinkResponse>(await res.json());
    } finally {
      clearTimeout(timeout);
    }
  },

  /**
   * Regenerate AI copy for an existing share link. Returns the new copy.
   * On generation failure returns success=false; UI keeps the existing cached
   * copy and shows a toast.
   */
  regenerateCopy: async (
    shareCode: string,
    body: { tone?: ShareLinkTone } = {},
  ): Promise<ApiResponse<{ copy: Required<ShareLinkCopy> }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 45000);
    try {
      const res = await fetch(`${API_BASE}/share-links/${shareCode}/regenerate-copy`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      return normalizeShareLinkResponse<{ copy: Required<ShareLinkCopy> }>(await res.json());
    } finally {
      clearTimeout(timeout);
    }
  },

  /** Revoke a share link. Soft-delete — re-minting the same (job, recruiter) reactivates. */
  revoke: async (shareCode: string, reason?: string): Promise<ApiResponse<unknown>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/share-links/${shareCode}`, {
      method: 'DELETE',
      headers,
      body: reason ? JSON.stringify({ reason }) : undefined,
    });
    return normalizeShareLinkResponse<unknown>(await res.json());
  },

  /** Stats for a share link — clicks, applications, hires, 7d/30d windows. */
  stats: async (
    shareCode: string,
  ): Promise<ApiResponse<{
    clicks: number;
    applications: number;
    hires: number;
    last7DaysClicks: number;
    last30DaysClicks: number;
  }>> => {
    const token = getAdminToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/share-links/${shareCode}/stats`, { method: 'GET', headers });
    return normalizeShareLinkResponse<{
      clicks: number;
      applications: number;
      hires: number;
      last7DaysClicks: number;
      last30DaysClicks: number;
    }>(await res.json());
  },
};

// ==================== MARKETPLACE ENDPOINTS (May 2026) ====================
// Recruiter-facing structured candidate search → reveal pipeline.
// See docs/MARKETPLACE_ARCHITECTURE.md.

export interface MarketplaceSearchFilters {
  /** Role-first search target. The isolated V2 adapter compiles this into the
   *  shared role intent; familyMode makes boost-vs-filter semantics explicit.
   *  It lives inside filters so saved searches retain the complete intent. */
  role?: {
    title: string;
    family: string | null;
    canonicalTitle?: string;
    familyMode?: 'boost' | 'filter';
  };
  /** V2 intent. New selections land in `required` (AND) by default. */
  skillIntent?: MarketplaceSkillIntent;
  /** Legacy flat skill selections. Multi-skill arrays are migrated to one V2
   *  required one-of group so the old overlap/ANY meaning remains runnable. */
  skills?: string[];
  experienceYears?: { min?: number; max?: number };
  salaryRange?: { min?: number; max?: number };
  /** Legacy 'current'/'expected' come from old saved searches and are
   *  honored verbatim; the UI no longer offers the toggle and sends
   *  'either' (current OR expected satisfies the range) for every new search. */
  salaryField?: 'current' | 'expected' | 'either';
  city?: string;
  locations?: string[];
  companies?: string[];
  education?: string[];
  noticeDaysMax?: number;
  activeWithinDays?: number;
  /** §4C structured facets (projection columns). Closed-list multi-select. */
  seniority?: string[];
  industry?: string[];
  workMode?: 'remote' | 'hybrid' | 'onsite';
  /** Saved-search provenance only. The request compiler strips this before
   *  sending a search spec, while opaque saved-search JSON retains it. */
  sourceContext?: MarketplaceJobSource;
}

export type MarketplaceSortBy = MarketplaceSearchSort;
export type MarketplaceSearchSpecV2 = MarketplaceSearchSpecV2Wire;
export type MarketplaceSearchOverrides = MarketplaceSearchSpecOverrides;
export type MarketplaceSearchRequest = MarketplaceSearchV2ApiRequest;
export type MarketplaceSearchByJobRequest = MarketplaceSearchByJobApiRequest;

export type MarketplaceCandidateSource = MarketplaceSearchEnrichedResultV2['source'];

/** Exact public search row. Private ranking scores/components, raw match
 * explanations, exact activity timestamps, and geo distances deliberately do
 * not exist on this type. */
export interface MarketplaceCandidateResult extends MarketplaceSearchEnrichedResultV2 {
  /** Resume preview metadata — attached by the gateway on the SINGLE-candidate
   *  fetch only (absent from search-list results), and only when the tenant
   *  holds a candidate_tenants relationship (live/snapshot). `discovery` (no
   *  relationship) gets none — the INNER JOIN that produces these is the
   *  entitlement gate. Mirrors the full candidate page's resume behaviour. */
  resumeUrl?: string | null;
  resumeId?: string | null;
  mimeType?: string | null;
  fileName?: string | null;
}

/** A direct candidate-overlay fetch has no active search query, so it cannot
 * mint or inherit `skillCoverage`. Search previews and owned detail views keep
 * their representations separate. */
export type MarketplaceCandidateOverlayResult = Omit<
  MarketplaceCandidateResult,
  'skillCoverage'
> & {
  skillCoverage?: MarketplaceSearchSkillCoverage;
};

/** The shared response refinements require a typed error on failure and forbid
 * one on success. Preserve that validated relationship for UI consumers. */
export type MarketplaceSearchResponse = Omit<
  MarketplaceSearchByJobV2Response,
  'success' | 'results' | 'error'
> & {
  results: MarketplaceCandidateResult[];
} & (
  | { success: true; error?: never }
  | { success: false; error: NonNullable<MarketplaceSearchByJobV2Response['error']> }
);

type MarketplaceOwnedJobsData = JobPickerPage;

/** A validated, bounded page with explicit continuation. */
export type MarketplaceOwnedJobsResponse =
  | (ApiResponse<MarketplaceOwnedJobsData> & { success: true; data: MarketplaceOwnedJobsData })
  | (ApiResponse<never> & { success: false });

export interface MarketplaceRevealQuoteRequest {
  candidateId: string;
  unlockType: 'discovery' | 'refresh';
  /** Captured when the recruiter clicks Reveal. The gateway compares these
   *  values with the verified JWT before performing any mutation. They are a
   *  stale-auth guard only, never an authority source. */
  expectedTenantId: string;
  expectedUserId: string;
}

export interface MarketplaceRevealQuoteResponse {
  quoteToken: string;
  candidateId: string;
  unlockType: 'discovery' | 'refresh';
  cost: number;
  candidateVersion: number;
  expiresAt: string;
}

export interface MarketplaceRevealRequest {
  /** Opaque, short-lived, exact price/version/policy quote minted by the
   * gateway. Candidate identity and cost are derived from this signed token. */
  quoteToken: string;
  expectedTenantId: string;
  expectedUserId: string;
}

export interface MarketplaceRevealResponse {
  ledgerEntryId: string;
  revealRecordId: string;
  creditsCharged: number;
  balanceAfter: number;
  dataVersion: number;
  isReplay: boolean;
}

// Marketplace endpoints are recruiter-only — they use the admin auth token
// (`vhire_admin_token`) NOT the candidate token (`vhire_token`). fetchApi
// reads only `vhire_token`, so we use direct fetch + getAdminToken here.
async function fetchMarketplace<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  // Lazy import avoids a circular dep between lib/api.ts and lib/admin-auth-context
  const { getAdminToken } = await import('./admin-auth-context');
  const token = getAdminToken();
  if (!token) {
    // Client-side failure before any HTTP call — surface as a real failure
    // (status 0) so status-branching callers fall through to their generic error.
    return { success: false, error: 'Not authenticated (no admin token)', status: 0 };
  }
  const deadline = createRequestDeadline({
    signal: options.signal,
    timeoutReason: new DOMException('Request timed out', 'TimeoutError'),
    timeoutMs: endpoint === '/marketplace/search' || endpoint === '/marketplace/search/by-job'
      ? RECRUITER_SEARCH_BROWSER_TIMEOUT_MS : DEFAULT_FETCH_TIMEOUT_MS,
  });
  let received = false;
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      signal: deadline.signal,
    });
    received = true;
    if (!response.ok) reportApiFailure(endpoint, options, { response });
    const data = await response.json().catch(() => ({}));
    // Always attach the HTTP status (matching adminFetchApi) so callers can map
    // specific error states — e.g. 503 ENC_KEY_MISSING, 409 verified-day — by
    // status. Without this the whole network section's status-branching is dead.
    if (!response.ok) {
      const rawError = (data as { error?: unknown }).error;
      const topLevel = data && typeof data === 'object'
        ? data as { code?: unknown; message?: unknown }
        : null;
      const typedError = rawError && typeof rawError === 'object'
        ? rawError as { code?: unknown; message?: unknown; issues?: unknown }
        : null;
      return {
        success: false,
        ...(endpoint === '/marketplace/search/by-job' ? { data: data as T } : {}),
        error:
          typeof rawError === 'string'
            ? rawError
            : typeof typedError?.message === 'string'
              ? typedError.message
              : typeof topLevel?.message === 'string'
                ? topLevel.message
              : `Request failed (${response.status})`,
        ...(typeof typedError?.code === 'string'
          ? { errorCode: typedError.code }
          : typeof topLevel?.code === 'string'
            ? { errorCode: topLevel.code }
            : {}),
        ...(Array.isArray(typedError?.issues)
          ? { issues: typedError.issues.filter((issue): issue is string => typeof issue === 'string').slice(0, 20) }
          : {}),
        status: response.status,
      };
    }
    return { success: true, data: data as T, status: response.status };
  } catch (error) {
    if (!received) reportApiFailure(endpoint, options, { error });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0, // network / timeout — not an HTTP status; callers treat as a real failure.
    };
  } finally {
    deadline.dispose();
  }
}

export const marketplaceApi = {
  /** Same recruiter visibility as Jobs; only a bounded page of picker fields. */
  listOwnedJobs: async (tenantId: string, options?: { signal?: AbortSignal; search?: string; cursor?: string }): Promise<MarketplaceOwnedJobsResponse> => {
    const request = JobPickerRequestSchema.safeParse({ search: options?.search ?? '', cursor: options?.cursor });
    if (!request.success) return { success: false, status: 400, error: 'Invalid job picker search or continuation.' };
    const params = new URLSearchParams({ view: JOB_PICKER_VIEW });
    if (request.data.search) params.set('search', request.data.search);
    if (request.data.cursor) params.set('cursor', request.data.cursor);
    const response = await fetchMarketplace<unknown>(
      `/tenants/${encodeURIComponent(tenantId)}/jobs?${params}`, { signal: options?.signal },
    );
    if (!response.success) return { ...response, success: false, data: undefined };
    const page = JobPickerPageSchema.safeParse(response.data);
    if (!page.success) return { success: false, status: response.status, error: 'Job list returned an invalid page. Please retry.' };
    return { ...response, success: true, data: page.data };
  },
  /**
   * Per-reveal pricing for the tenant's tier — real discovery/refresh costs so
   * Reveal buttons don't show a hardcoded estimate.
   */
  getRevealPricing: async (
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<{ discoveryCost: number; refreshCost: number }>> => {
    return fetchMarketplace('/marketplace/pricing', { signal: options?.signal });
  },

  /**
   * Canonical-skill typeahead from our taxonomy (`/api/talent/skills/search`).
   * Admin-auth so it works from the recruiter marketplace context. Powers the
   * SkillMultiSelect filter — validated skills, not free text.
   */
  searchSkills: async (
    query: string,
  ): Promise<ApiResponse<{ skills: Array<{
    id?: string;
    name: string;
    category?: string | null;
    isComposite?: boolean;
  }> }>> => {
    return fetchMarketplace(`/talent/skills/search?q=${encodeURIComponent(query)}`);
  },

  /** Pool supply per skill ("Java · 1,856") for the filter typeahead. */
  skillCounts: async (
    canonicalSkillIds: string[],
  ): Promise<ApiResponse<{ counts: Record<string, number> }>> => {
    return fetchMarketplace(`/marketplace/skill-counts?skillIds=${encodeURIComponent(canonicalSkillIds.join(','))}`);
  },

  /**
   * Search candidates by structured filters + optional free-text.
   * Returns enriched results by default (with version-aware overlay).
   * Uses admin auth (vhire_admin_token).
   */
  search: async (
    request: MarketplaceSearchRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<MarketplaceSearchResponse>> => {
    const response = await fetchMarketplace<unknown>('/marketplace/search', {
      method: 'POST',
      body: JSON.stringify(request),
      signal: options?.signal,
    });
    if (!response.success) {
      return {
        success: false,
        error: response.error,
        errorCode: response.errorCode,
        issues: response.issues,
        status: response.status,
      };
    }
    const parsed = MarketplaceSearchGatewayV2ResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      return {
        success: false,
        error: 'Marketplace returned an invalid V2 search response',
        status: response.status,
      };
    }
    return {
      success: true,
      data: parsed.data as MarketplaceSearchResponse,
      status: response.status,
    };
  },

  /** Compile canonical job intent server-side and search it. Uses recruiter
   * auth, works for drafts, and returns the effective spec + source version. */
  searchByJob: async (
    request: MarketplaceSearchByJobRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<MarketplaceSearchResponse>> => {
    const response = await fetchMarketplace<unknown>('/marketplace/search/by-job', {
      method: 'POST',
      body: JSON.stringify(request),
      signal: options?.signal,
    });
    const parsed = MarketplaceSearchByJobV2ResponseSchema.safeParse(response.data);
    if (!response.success) {
      return {
        success: false,
        ...(parsed.success && !parsed.data.success ? { data: parsed.data as MarketplaceSearchResponse } : {}),
        error: response.error,
        errorCode: response.errorCode,
        issues: response.issues,
        status: response.status,
      };
    }
    if (!parsed.success) {
      return {
        success: false,
        error: 'Marketplace returned an invalid V2 job-search response',
        status: response.status,
      };
    }
    return {
      success: true,
      data: parsed.data as MarketplaceSearchResponse,
      status: response.status,
    };
  },

  /**
   * Fetch a short-lived candidate-specific quote. Confirmation is disabled
   * until this succeeds; global tier pricing is informational only.
   */
  getRevealQuote: async (
    request: MarketplaceRevealQuoteRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<MarketplaceRevealQuoteResponse>> => {
    return fetchMarketplace('/marketplace/reveal/quote', {
      method: 'POST',
      body: JSON.stringify(request),
      signal: options?.signal,
    });
  },

  /**
   * Spend credits using the exact quote the recruiter confirmed.
   * Idempotent — retries on the same (tenant, candidate, version) return
   * the original result without double-charging.
   */
  reveal: async (
    request: MarketplaceRevealRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<MarketplaceRevealResponse>> => {
    return fetchMarketplace('/marketplace/reveal', {
      method: 'POST',
      body: JSON.stringify(request),
      signal: options?.signal,
    });
  },

  /**
   * Fetch a single candidate via the version overlay. Returns 'live' for
   * current tenants, 'snapshot' for stale, 'discovery' for no-relationship.
   */
  fetchCandidate: async (
    candidateId: string,
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<{ candidate: MarketplaceCandidateOverlayResult }>> => {
    return fetchMarketplace(`/marketplace/candidates/${candidateId}`, {
      signal: options?.signal,
    });
  },

  /** Minimal Marketplace balance projection. The server derives tenant
   * authority from the JWT and intentionally returns no other product balance
   * or reveal audit history. */
  getCreditBalance: async (
    options?: { signal?: AbortSignal },
  ): Promise<ApiResponse<{ dataAccess: number }>> => {
    return fetchMarketplace('/marketplace/credit-balance', { signal: options?.signal });
  },
};

// ============================================================================
// Marketplace saved searches — per-recruiter, per-tenant, backed by Neon
// (migration 047). Uses the same admin-auth fetch helper as marketplaceApi
// because saved searches are recruiter-only.
// ============================================================================

interface SavedSearchServerItem {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  freeText: string;
  sourceLabel: string | null;
  createdAt: number;
  updatedAt: number;
}

export const marketplaceSavedSearchesApi = {
  list: async (): Promise<ApiResponse<{ items: SavedSearchServerItem[] }>> => {
    return fetchMarketplace('/marketplace/saved-searches');
  },

  create: async (input: {
    /** Captured owner scope. The gateway only uses it to reject an auth
     *  transition that happened while getAdminToken() was resolving. */
    expectedTenantId: string;
    expectedUserId: string;
    /** Stable across retries so a lost response cannot create a duplicate. */
    clientRequestId: string;
    name: string;
    filters: Record<string, unknown>;
    freeText: string;
    sourceLabel?: string | null;
  }): Promise<ApiResponse<{ item: SavedSearchServerItem }>> => {
    return fetchMarketplace('/marketplace/saved-searches', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  delete: async (
    id: string,
  ): Promise<ApiResponse<{ deleted: boolean }>> => {
    return fetchMarketplace(`/marketplace/saved-searches/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============================================================================
// Partner Network (employer-side) — recruiter/tenant-admin surface. All routes
// live behind the gateway flag NETWORK_DIRECTORY_ENABLED (404 when off) and
// require a tenant-admin JWT; the server derives the tenant from the token, so
// NEVER pass tenantId in a request. Same admin-token mechanics as
// fetchMarketplace (envelope return, no throw) — pages surface `error` via
// toast + retry. See src/lib/network-types.ts for the exact shapes.
//
// FEE-VISIBILITY: the employer sees employer-cost fields ONLY. BidSummary
// carries raw vendor `feeTerms` (typed `unknown`) — never render it directly.
// ============================================================================

/** Cursor/limit → querystring (skips empty values). Shared by the GET lists. */
function networkQuery(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Raw-row normalizers. Three tenant-read RPCs — listSettlementInvoicesForTenant,
// listPlacementsForTenant, listPublishApprovals — return RAW snake_case Postgres
// rows verbatim (verified 2026-07-23 against apps/vhire-network-service/src/
// index.ts + tenant-reads.ts), unlike engagements/timesheets which the service
// maps to camelCase. We normalize here so components consume one camelCase shape.
// BIGINT paise arrive as numeric STRINGS (postgres.js default) — coerce to number.
// ---------------------------------------------------------------------------

function nullableString(v: unknown): string | null {
  if (v == null) return null;
  return typeof v === 'string' ? v : String(v);
}

/** Coerce a BIGINT paise value (numeric string, or number) to a finite number. */
function toPaiseNumber(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toSettlementInvoiceSummary(r: Record<string, unknown>): SettlementInvoiceSummary {
  return {
    id: String(r.id ?? ''),
    placementId: nullableString(r.placement_id),
    amountPaise: toPaiseNumber(r.amount_paise),
    currency: nullableString(r.currency),
    status: nullableString(r.status),
    issuedAt: nullableString(r.issued_at),
    dueAt: nullableString(r.due_at),
    paidAt: nullableString(r.paid_at),
  };
}

function toPlacementSummary(r: Record<string, unknown>): PlacementSummary {
  return {
    id: String(r.id ?? ''),
    jobId: nullableString(r.job_id),
    jobTitle: nullableString(r.job_title),
    candidateName: nullableString(r.candidate_name),
    vendorOrgId: nullableString(r.vendor_org_id),
    feeModel: nullableString(r.fee_model),
    feePaise: toPaiseNumber(r.employer_charge_paise),
    currency: nullableString(r.currency),
    placedAt: nullableString(r.created_at),
  };
}

/** Coerce a Postgres `numeric` (arrives as a string), number, or null → number|null. */
function toNumOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize a RAW fee_agreements row (listFeeAgreements passes rows through
 * verbatim — snake_case; the pct columns are Postgres `numeric` → STRINGS) into a
 * camelCase FeeAgreement with number-coerced percentages. PLATFORM-ADMIN ONLY —
 * this is the one surface allowed full commercial visibility.
 */
function toFeeAgreement(r: Record<string, unknown>): FeeAgreement {
  const c2h = r.c2h_conversion_fee_config;
  return {
    id: String(r.id ?? ''),
    vendorOrgId: nullableString(r.vendor_org_id),
    engagementType: nullableString(r.engagement_type),
    feeModel: nullableString(r.fee_model),
    employerSellPct: toNumOrNull(r.employer_sell_pct),
    vendorBuyPct: toNumOrNull(r.vendor_buy_pct),
    contractFeePct: toNumOrNull(r.contract_fee_pct),
    c2hConversionFeeConfig:
      c2h && typeof c2h === 'object' && !Array.isArray(c2h) ? (c2h as Record<string, unknown>) : null,
    currency: nullableString(r.currency),
    status: nullableString(r.status),
    version: toNumOrNull(r.version),
    effectiveFrom: nullableString(r.effective_from),
    effectiveTo: nullableString(r.effective_to),
    createdByPlatformAdminId: nullableString(r.created_by_platform_admin_id),
    createdAt: nullableString(r.created_at),
  };
}

function toPublishApprovalSummary(r: Record<string, unknown>): PublishApprovalSummary {
  return {
    id: String(r.id ?? ''),
    jobId: String(r.target_job_id ?? ''),
    jobTitle: nullableString(r.job_title),
    partnerOrgIds: Array.isArray(r.target_partner_org_ids)
      ? (r.target_partner_org_ids as unknown[]).map((x) => String(x))
      : [],
    status: String(r.status ?? ''),
    createdAt: nullableString(r.created_at),
    decidedAt: nullableString(r.decided_at),
  };
}

export const networkApi = {
  // -------- Vendor directory (network:view) --------
  /**
   * Search the cross-tenant vendor directory (POST, keyset-paginated). No PII,
   * no fee fields in the response — safe to render whole.
   */
  searchVendors: async (
    request: VendorSearchRequest = {},
  ): Promise<ApiResponse<VendorSearchResponse>> =>
    fetchMarketplace('/network/vendors', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Publish to partners (network:publish) --------
  /**
   * File a PENDING publish-approval for a job against the given partner orgs.
   * Does NOT fan out — distribution stays gated until a network:approve holder
   * decides it. Response: { approvalId, isReplay }.
   */
  publishToPartners: async (
    jobId: string,
    request: PublishToPartnersRequest,
  ): Promise<ApiResponse<PublishToPartnersResponse>> =>
    fetchMarketplace(`/jobs/${encodeURIComponent(jobId)}/publish-to-partners`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Publish-approvals inbox (network:approve) --------
  /**
   * List publish-approval requests (inbox). GET /api/network/publish-approvals
   * (verified live). The RPC returns RAW rows — normalized to camelCase here.
   */
  listPublishApprovals: async (
    params: { status?: string; cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<ListPublishApprovalsResponse>> => {
    const res = await fetchMarketplace<{
      approvals?: Array<Record<string, unknown>>;
      nextCursor?: string | null;
    }>(`/network/publish-approvals${networkQuery(params)}`);
    if (!res.success || !res.data) {
      return { success: false, error: res.error, status: res.status };
    }
    return {
      success: true,
      data: {
        approvals: (res.data.approvals ?? []).map(toPublishApprovalSummary),
        nextCursor: res.data.nextCursor ?? null,
      },
    };
  },

  /** Approve or reject a publish request (forward-only; already-decided → 409). */
  decidePublishApproval: async (
    approvalId: string,
    request: PublishDecisionRequest,
  ): Promise<ApiResponse<PublishDecisionResponse>> =>
    fetchMarketplace(`/network/publish-approvals/${encodeURIComponent(approvalId)}/decision`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Distribute (network:distribute) --------
  /**
   * Distribute an APPROVED job to its top-ranked matched vendors. Gated
   * server-side behind an approved publish request (PublishNotApprovedError
   * otherwise — surface that failure). Response: { distributed, vendorOrgIds }.
   */
  distributeJob: async (
    jobId: string,
    request: DistributeJobRequest = {},
  ): Promise<ApiResponse<DistributeJobResponse>> =>
    fetchMarketplace(`/jobs/${encodeURIComponent(jobId)}/distribute`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Bids (bids:review) --------
  /**
   * Review the vendor bids on the employer's OWN distributed job (tenant-
   * scoped). ⚠️ BidSummary.feeTerms is raw vendor JSONB — never render raw.
   */
  listBids: async (
    jobId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<ListBidsResponse>> =>
    fetchMarketplace(`/jobs/${encodeURIComponent(jobId)}/bids${networkQuery(params)}`),

  // -------- Fill score (network:view) --------
  /**
   * Volume-gated, explainable HEURISTIC (not a guaranteed prediction) of a
   * job's fill likelihood. Two states: { available:false } while cold, or
   * { available:true, score, components } when warm. Handle both.
   */
  getFillScore: async (
    jobId: string,
  ): Promise<ApiResponse<FillScoreResult>> =>
    fetchMarketplace(`/jobs/${encodeURIComponent(jobId)}/fill-score`),

  // -------- Engagements (bids:review) — contract-first --------
  listEngagements: async (
    params: ListEngagementsRequest = {},
  ): Promise<ApiResponse<ListEngagementsResponse>> =>
    fetchMarketplace(`/network/engagements${networkQuery(params)}`),

  // -------- Timesheets (bids:review) — contract-first --------
  listTimesheets: async (
    params: ListTimesheetsRequest = {},
  ): Promise<ApiResponse<ListTimesheetsResponse>> =>
    fetchMarketplace(`/network/timesheets${networkQuery(params)}`),

  /**
   * Verify or reject a submitted timesheet. NOTE: RPC exists but the HTTP route
   * is NOT wired in this checkout — may 404 until the backend branch lands;
   * gate the action UI accordingly.
   */
  verifyTimesheet: async (
    timesheetId: string,
    request: VerifyTimesheetRequest,
  ): Promise<ApiResponse<VerifyTimesheetResponse>> =>
    fetchMarketplace(`/network/timesheets/${encodeURIComponent(timesheetId)}/verify`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Settlement (commission:view) --------
  /**
   * Settlement invoices the EMPLOYER is billed. GET /api/network/settlement/invoices
   * (verified live). The RPC returns RAW rows — normalized to camelCase here, and
   * the BIGINT `amount_paise` (a numeric string) is coerced to a number. Money is
   * PAISE; <PaiseAmount> converts paise → ₹ at render.
   */
  listSettlementInvoices: async (
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<ListSettlementInvoicesResponse>> => {
    const res = await fetchMarketplace<{
      invoices?: Array<Record<string, unknown>>;
      nextCursor?: string | null;
    }>(`/network/settlement/invoices${networkQuery(params)}`);
    if (!res.success || !res.data) {
      return { success: false, error: res.error, status: res.status };
    }
    return {
      success: true,
      data: {
        invoices: (res.data.invoices ?? []).map(toSettlementInvoiceSummary),
        nextCursor: res.data.nextCursor ?? null,
      },
    };
  },

  /**
   * Placements (commission:view). GET /api/network/placements (verified live). The
   * RPC returns RAW rows — normalized to camelCase here, and the BIGINT
   * `employer_charge_paise` is coerced to a number. EMPLOYER charge only.
   */
  listPlacements: async (
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<ListPlacementsResponse>> => {
    const res = await fetchMarketplace<{
      placements?: Array<Record<string, unknown>>;
      nextCursor?: string | null;
    }>(`/network/placements${networkQuery(params)}`);
    if (!res.success || !res.data) {
      return { success: false, error: res.error, status: res.status };
    }
    return {
      success: true,
      data: {
        placements: (res.data.placements ?? []).map(toPlacementSummary),
        nextCursor: res.data.nextCursor ?? null,
      },
    };
  },

  // ════════════════════════════════════════════════════════════════════════
  // P4 — VENDOR-SIDE (tenant-admin JWT, `vendor` role). All server-derive the
  // vendor org from the token (NEVER send vendorTenantId). The vendor sees its
  // OWN rate/payouts only — never employer cost/margin/spread. These RPCs already
  // return camelCase, so the fns are typed pass-throughs. See src/lib/
  // network-types.ts for the exact shapes.
  // ════════════════════════════════════════════════════════════════════════

  // -------- Vendor profile (vendor:manage) --------
  /** GET the authed vendor org's OWN marketplace profile (null if none yet). */
  getVendorProfile: async (): Promise<ApiResponse<GetVendorProfileResponse>> =>
    fetchMarketplace('/network/vendor-profile'),

  /**
   * PUT (create/replace) the vendor's own profile. `verificationStatus` is
   * IGNORED here (a vendor can never self-verify) — send only the settable subset.
   */
  updateVendorProfile: async (
    profile: VendorProfileInput,
  ): Promise<ApiResponse<UpsertVendorProfileResponse>> =>
    fetchMarketplace('/network/vendor-profile', {
      method: 'PUT',
      body: JSON.stringify({ profile }),
    }),

  // -------- Vendor job feed (vendor:bid) --------
  /**
   * Jobs the vendor may act on (approved partner-publish ∪ distributed-to). Each
   * row carries the vendor's OWN resolved rate (vendorRate) — never employer cost.
   */
  listMyJobs: async (
    params: ListMyJobsRequest = {},
  ): Promise<ApiResponse<ListMyJobsResponse>> =>
    fetchMarketplace(`/network/my/jobs${networkQuery(params)}`),

  // -------- Vendor engagements (vendor:bid) --------
  /**
   * The vendor's own engagements. `rateModel` is the vendor's own terms (raw
   * JSONB) — narrow explicitly, never spread into the DOM.
   */
  listMyEngagements: async (
    params: ListMyEngagementsRequest = {},
  ): Promise<ApiResponse<ListMyEngagementsResponse>> =>
    fetchMarketplace(`/network/my/engagements${networkQuery(params)}`),

  // -------- Vendor payouts (vendor:bid) --------
  /**
   * The vendor's payouts — vendor-side money ONLY (gross/tds/net in PAISE). No
   * employer charge/margin. Paise arrive as numbers (coerced server-side).
   */
  listMyPayouts: async (
    params: ListMyPayoutsRequest = {},
  ): Promise<ApiResponse<ListMyPayoutsResponse>> =>
    fetchMarketplace(`/network/my/payouts${networkQuery(params)}`),

  // -------- Payout account (vendor:manage) --------
  /** GET the MASKED payout account (last-4 + kyc + provisioning state). */
  getPayoutAccount: async (): Promise<ApiResponse<GetPayoutAccountResponse>> =>
    fetchMarketplace('/network/payout-account'),

  /**
   * PUT the vendor's bank details (AES-256 encrypted server-side, never echoed).
   * Returns 503 ENC_KEY_MISSING until the prod PAYOUT_ENC_KEY secret is set —
   * surface that with honest copy ("payouts not yet enabled") and let the vendor
   * retry later. DO NOT log or persist `input.accountNumber` after submit.
   */
  updatePayoutAccount: async (
    input: PayoutAccountInput,
  ): Promise<ApiResponse<UpsertPayoutAccountResponse>> =>
    fetchMarketplace('/network/payout-account', {
      method: 'PUT',
      body: JSON.stringify({ accountDetails: input }),
    }),

  // -------- Submit candidate (vendor:manage) --------
  /**
   * Submit a candidate against an employer's job. The resume is NOT a file upload
   * — pass the apply-flow candidateData plus optional parsed-resume SIGNALS
   * (resumeParsedData). vendorTenantId + employerTenantId are derived server-side.
   * 403 VENDOR_SUBMIT_FORBIDDEN if the gate (approval + relationship) fails.
   */
  submitCandidate: async (
    jobId: string,
    request: SubmitCandidateRequest,
  ): Promise<ApiResponse<SubmitCandidateResponse>> =>
    fetchMarketplace(`/network/jobs/${encodeURIComponent(jobId)}/submit-candidate`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // -------- Record timesheet (vendor:bid) --------
  /**
   * Log a worked day against the vendor's OWN engagement (submittedBy +
   * vendorTenantId are token-derived server-side). 409 DAY_ALREADY_VERIFIED on a
   * verified-day overwrite — surface it.
   */
  recordTimesheet: async (
    engagementId: string,
    request: RecordTimesheetRequest,
  ): Promise<ApiResponse<RecordTimesheetResponse>> =>
    fetchMarketplace(`/network/engagements/${encodeURIComponent(engagementId)}/timesheets`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // ════════════════════════════════════════════════════════════════════════
  // P5 — PLATFORM-ADMIN (platform-admin JWT + requirePlatformPermission). The one
  // surface allowed FULL commercial visibility (employer cost, vendor payout,
  // margin, spread). The Bearer transport is the same `vhire_admin_token`; the
  // gateway differentiates via requireAuth('platform-admin'). See network-types.ts.
  // ════════════════════════════════════════════════════════════════════════

  // -------- Fee agreements (platform:commercial:view / :manage) --------
  /**
   * List fee agreements (platform commercial config). GET returns RAW snake_case
   * rows — normalized to camelCase FeeAgreement here (pct `numeric` strings →
   * number|null). Full commercial visibility is intentional (platform admin only).
   */
  listFeeAgreements: async (
    params: ListFeeAgreementsRequest = {},
  ): Promise<ApiResponse<ListFeeAgreementsResponse>> => {
    const res = await fetchMarketplace<{
      agreements?: Array<Record<string, unknown>>;
      nextCursor?: string | null;
    }>(`/platform-admin/fee-agreements${networkQuery(params as Record<string, unknown>)}`);
    if (!res.success || !res.data) {
      return { success: false, error: res.error, status: res.status };
    }
    return {
      success: true,
      data: {
        agreements: (res.data.agreements ?? []).map(toFeeAgreement),
        nextCursor: res.data.nextCursor ?? null,
      },
    };
  },

  /**
   * Upsert a fee agreement (immutable, effective-dated versioning). Rejects a
   * retroactive effectiveFrom (409 RETROACTIVE_EFFECTIVE_FROM) / a conflicting
   * active agreement (409 CONFLICT) / bad terms (400 INVALID_ARGS). The acting
   * admin (createdByPlatformAdminId) is token-derived server-side — never sent.
   */
  upsertFeeAgreement: async (
    request: UpsertFeeAgreementRequest,
  ): Promise<ApiResponse<UpsertFeeAgreementResponse>> =>
    fetchMarketplace('/platform-admin/fee-agreements', {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  // -------- Vendor verification queue (platform:commercial:view) --------
  /**
   * List vendor profiles for the verification queue (vendor_profiles + tenant
   * name, verification_status filter, keyset cursor). NOT the cross-tenant
   * directory (searchVendors) — this surfaces pending/unverified vendors too.
   */
  listPlatformVendors: async (
    params: ListPlatformVendorsRequest = {},
  ): Promise<ApiResponse<ListPlatformVendorsResponse>> =>
    fetchMarketplace(`/platform-admin/vendors${networkQuery(params)}`),

  // -------- Vendor verification (platform:commercial:manage) --------
  /**
   * Move a vendor org's verification_status (the trust state a vendor can never
   * self-set). 404 VENDOR_NOT_FOUND if the vendor has no profile; 400 INVALID_ARGS
   * on a bad status. The acting admin id is token-derived server-side.
   */
  setVendorVerification: async (
    vendorOrgId: string,
    request: SetVendorVerificationRequest,
  ): Promise<ApiResponse<SetVendorVerificationResponse>> =>
    fetchMarketplace(`/platform-admin/vendors/${encodeURIComponent(vendorOrgId)}/verification`, {
      method: 'PUT',
      body: JSON.stringify(request),
    }),

  // -------- Generate contract invoice (platform:commercial:manage) --------
  /**
   * Manually emit ONE engagement.invoice_due for (engagement, period). 409
   * PERIOD_OVERLAP if the period overlaps an existing invoice; 404
   * ENGAGEMENT_NOT_FOUND if the engagement is unknown.
   */
  generateInvoice: async (
    engagementId: string,
    request: GenerateInvoiceRequest,
  ): Promise<ApiResponse<GenerateInvoiceResponse>> =>
    fetchMarketplace(`/network/engagements/${encodeURIComponent(engagementId)}/generate-invoice`, {
      method: 'POST',
      body: JSON.stringify(request),
    }),
};
